import EditContext from '@/ViewModels/Contexts/EditContext';
import GenericObject from '@/Model/GenericObject';
import type DetailViewViewModel from '@/ViewModels/Views/DetailViewViewModel';
import NavigationService from '@/Management/NavigationService';
import RADApplication from '@/Management/RADApplication';
import ResourceManager from '@/Management/ResourceManager';
import ComponentManager from '@/Management/ComponentManager';
import UIListener from '@/UIListener/UIListener';
import RADEnvironmentProxy from '@/Communication/RADEnvironmentProxy';
import Messenger from '@/Utility/Messenger';
import Config from '@/Management/GlobalConfiguration';
import FrontController from '@/Management/FrontController';
import * as CoreFrameDTOs from "coreframetypings/dto";
import type TypeSelectorWindowViewModelType from '@/ViewModels/Controls/TypeSelectorWindowViewModel';
import type TypeSelectorWindowType from '@/Controls/TypeSelectorWindow';
import type MessageBoxViewModelType from '@/ViewModels/Controls/MessageBoxViewModel';
import type ToolBarViewModelType from '@/ViewModels/Controls/ToolBarViewModel';
import type LinkViewModelType from '@/ViewModels/Controls/LinkViewModel';
import type LabelViewModelType from '@/ViewModels/Controls/LabelViewModel';
import type ImageViewModelType from '@/ViewModels/Controls/ImageViewModel';
import Utilities from '@/Utility/Utilities';
import { ISortedValidationMessages, IValidationMessage, ResultDTO } from '#/DefaultViewExtensionInterfaces';
import type ListViewViewFactory from '@/Factories/ViewFactories/ListViewViewFactory';
import ControlFactory from '@/Factories/ControlFactory';
import type ControlViewModel from '@/ViewModels/ControlViewModel';
import ChangeHeaderTitleMessage from '@/Messages/ChangeHeaderTitleMessage';
import DetailViewEnteredMessage from '@/Messages/DetailViewEnteredMessage';
import DetailViewExitedMessage from '@/Messages/DetailViewExitedMessage';
import type LinkViewModel from '@/ViewModels/Controls/LinkViewModel';
import ButtonViewModelBuilder from '@/Builder/ButtonViewModelBuilder';
import AssociationHelper from '@/Utility/Association/AssociationHelper';
import FilterChipsChangedMessage from '@/Components/Filter/FilterChipsChangedMessage';
import Constants from '@/AppConstants';
import BusyCounter from '@/Utility/BusyCounter';
import { IResultDTO } from "coreframetypings/dto";
import GlobalConfiguration from "@/Management/GlobalConfiguration";

type OnBeforeApplyInterceptorCallbackResult = {
    /**
     * Indicates whether the callback was successful and applying is possible
     */
    success: boolean;

    /**
     * An error message which gets shown if the result is not successful
     */
    errorMessage?: string;
};
type OnBeforeApplyInterceptorCallback = () => Promise<OnBeforeApplyInterceptorCallbackResult>;
type OnBeforeApplyInterceptor = {
    /**
     * Indicates whether the interceptor should be retained after execution
     */
    retain: boolean;

    callback: OnBeforeApplyInterceptorCallback;
};
type OnAfterApplyInterceptor = (isClosing: boolean) => void;

export default class DetailViewContext extends EditContext {

    private TypeSelectorWindowViewModel = ComponentManager.getViewModel<typeof TypeSelectorWindowViewModelType>('TypeSelectorWindowViewModel');
    private TypeSelectorWindow = ComponentManager.getControl<typeof TypeSelectorWindowType>('TypeSelectorWindow');
    private MessageBoxViewModel = ComponentManager.getViewModel<typeof MessageBoxViewModelType>('MessageBoxViewModel');
    private ToolBarViewModel = ComponentManager.getViewModel<typeof ToolBarViewModelType>('ToolBarViewModel');
    protected LinkViewModel = ComponentManager.getViewModel<typeof LinkViewModelType>('LinkViewModel');
    protected LabelViewModel = ComponentManager.getViewModel<typeof LabelViewModelType>('LabelViewModel');
    protected ImageViewModel = ComponentManager.getViewModel<typeof ImageViewModelType>('ImageViewModel');

    protected isOnStackNavigation: boolean;

    /**
    * Der aufrufende DetailViewContext
    */
    private __caller: any;

    /**
    * Der Name, falls es sich um einen nestedContext handelt
    */
    private _nestedContextName: string;

    /**
    * Das übergebene DetailViewContextDTO
    */
    private _detailViewContextDTO: CoreFrameDTOs.IDetailViewContextDTO;

    /**
    * Die übergebene Objekt-Id
    */
    private _pdoId: string;

    /**
    * Der übergebene PdTypeName
    */
    private _pdTypeName: string;

    /**
    * Die übergebene Context-Pdo-Id
    */
    private _contextPdoId: string;

    /**
    * Der übergebene Context-PdTypeName
    */
    private _contextPdTypeName: string;

    /**
    * Der übergebene Context-RelationName
    */
    private _contextRelationName: string;

    /**
    * Die übergebenen Context-Parameters
    */
    protected _contextParameters: any;

    /**
    * Gibt an, ob die DetailView sich im SummaryViewMode befindet
    */
    private _isSummaryViewMode: boolean;

    /**
    * Eine Liste der Items in diesem Kontext
    */
    private _items: Array<string>;

    /**
    * Eine Liste mit Item-Namen deren Rechte reduziert wurden
    */
    private _reducedRights: Array<string>;

    /**
    * Ein Computed-Property, welches angibt, ob der Kontext modifiziert wurde
    */
    private _isModified: KnockoutComputed<boolean>;

    /**
    * Signalisiert, ob der DV-Kontext committed wurde.
    */
    private _hasObjectChanged: boolean;

    /**
    * Ein Computed-Property, welches angibt, ob der Kontext ausstehende Änderungen hat
    */
    private _hasPendingModifications: KnockoutObservable<boolean>;

    /**
    * Die Art der Aktion, die zum Erstellen dieses DetailViewContext geführt hat.
    */
    public action: string;

    /**
    * Ein Computed-Property, welches angibt, ob der Kontext geladen wurde
    */
    public isDetailViewReady: KnockoutComputed<boolean>;

    /**
     * Gibt an, ob der DetailViewContext (oder eines der Kinder) gesperrt ist
     */
    public isLocked: KnockoutComputed<boolean>;

    /**
    * Gibt an, ob die DetailView gerade geschlossen wird und schon eine weitere View zum Laden bereitsteht
    */
    protected _hasPendingDVC: boolean;

    protected _isContextClosed: boolean;
    private _isInit: KnockoutObservable<boolean>;

    /**
     * Gibt an, ob der Context gerade beendet wird
     */
    protected _isCanceling: boolean;
    private _subTypeSelectorWindowViewModel: TypeSelectorWindowViewModelType;
    private _subTypeSelectorWindow: TypeSelectorWindowType;

    /**
     * Die Id des Kontexts
     */
    public id: string;

    /**
     * Ein Observable, welches die DetailView beinhaltet
     */
    public detailView: KnockoutObservable<CoreFrameDTOs.IDetailViewDTO>;

    /**
     * Beinhaltet und verändert die Breite der LabelColumn in allen Layout-Factories des aktuellen Kontexts
     */
    public labelColumnWidth: KnockoutObservable<number>;

    /**
     * Das geladene DetailViewContextDTO
     */
    public detailViewContextDTO: CoreFrameDTOs.IDetailViewContextDTO;

    /**
     * Das aktuelle ViewModel
     */
    public viewModel: DetailViewViewModel;

    /**
     * Die ToolBar
     */
    public toolBar: ToolBarViewModelType;

    public isInit: KnockoutComputed<boolean>;
    public pageTitle: string;

    /**
     * Steuert, ob eine DV Readonly ist.
     */
    private forceReadonly: KnockoutObservable<boolean>;


    /**
     * Die im aktuellen Kontext verschachtelten Kontexte
     */
    public _nestedDetailViewContexts: KnockoutObservable<{ [key: string]: DetailViewContext }>;

    /**
     * Die im aktuellen Kontext verschachtelten InlineEditingKontexte
     */
    public _nestedInlineEditingContexts: KnockoutObservableArray<EditContext>;

    /**
     * Ein Observable, zur Verwaltung des Kontext-Refreshs
     */
    public _refresh: KnockoutObservable<any>;

    /**
     * Eine Liste der verschachtelten Kontext-DTOs
     */
    public _nestedDetailViewContextDTOs: Array<CoreFrameDTOs.IDetailViewContextDTO> & { _findByName?: (name: string) => CoreFrameDTOs.IDetailViewContextDTO };

    /**
     * Die Id eines ManagedStaticOperationCalls
     */
    public _callId: any;

    /**
     * Die zuletzt aufgerufene Aktion des Kontexts
     */
    public _latestDetailViewAction: string;

    /**
     * Hier kann die Anwendung einen Grund hinterlegen WARUM die DV geschlossen wurde
     */
    public _reasonForLeaving: any;

    /**
     * Eine Liste von Property-Namen, deren Wertänderungen unterdrückt werden
     */
    public _supressedProperties: Array<string>;

    public onBeforeApplyHandler: () => boolean | Promise<boolean>;
    private onBeforeApplyInterceptors: Record<string, OnBeforeApplyInterceptor>;
    private onAfterApplyInterceptors: OnAfterApplyInterceptor[];

    private hasNestedDetailViewContexts: KnockoutObservable<boolean>;

    public serializedFilters: any;

    protected autoInit: boolean;

    private internalIsModifiedForced: KnockoutObservable<boolean>;
    private externalIsModifiedForced: KnockoutObservable<boolean>;
    public useShortTransactions: KnockoutObservable<boolean>;

    public isBusy: KnockoutObservable<boolean>;
    private busyCounter: BusyCounter;

    private isDeleting = false;
    private isDeleted = false;

    /**
     * PublicUserData entries for the associations
     * Key: name of the association, Value: PublicUserData entry
     */
    public publicUserData: Record<string, string>;

    public constructor(params) {
        super(params);
        this.isBusy = this.observableOrDefault(false);
        this.isOnStackNavigation = false;
        this.__caller = params.caller;
        this._nestedContextName = params.nestedContextName;
        this._detailViewContextDTO = params.detailViewContextDTO;
        this._pdoId = params.pdoId;
        this._pdTypeName = params.pdTypeName;
        this._contextPdoId = params.contextPdoId;
        this._contextPdTypeName = params.contextPdTypeName;
        this._contextRelationName = params.contextRelationName;
        this._contextParameters = params.contextParameters;
        this._isSummaryViewMode = this.paramOrDefault(params.isSummaryViewMode, false);
        this._items = [];
        this._reducedRights = [];
        this._isModified = null;
        this._hasObjectChanged = false;
        this._hasPendingModifications = null;
        this.action = params.action;
        this.isDetailViewReady = null;
        this._hasPendingDVC = false;
        this._isContextClosed = false;
        this._isCanceling = false;
        this._isInit = this.observableOrDefault(false);
        this.isInit = this.defineComputed(() => { return this._isInit() });
        this.pageTitle = "";
        this.id = "-1";
        this.detailView = this.observableOrDefault(null);
        this.labelColumnWidth = this.observableOrDefault(params.labelColumnWidth, null);
        this.detailViewContextDTO = null;
        this.viewModel = params.viewModel;
        this.genericObject = null;
        this.toolBar = null;
        this._nestedDetailViewContexts = this.observableOrDefault({});
        this._nestedInlineEditingContexts = this.observableOrDefault([]) as KnockoutObservableArray<EditContext>;
        this._refresh = this.observableOrDefault(null).extend({ notify: 'always' });
        this._nestedDetailViewContextDTOs = [];
        this._callId = params.callId;
        this._latestDetailViewAction = 'Error';
        this._reasonForLeaving = null;
        this._supressedProperties = [];
        this.onBeforeApplyHandler = params.onBeforeApplyHandler;
        this.onBeforeApplyInterceptors = {};
        this.onAfterApplyInterceptors = [];
        this.hasNestedDetailViewContexts = this.observableOrDefault(false);
        this.isLocked = this.manageLockStatus();
        this.autoInit = this.paramOrDefault(params.autoInit, true);
        if (this.autoInit)
            this.initDetailViewContext();
        this.serializedFilters = params.serializedFilters || null;
        this.internalIsModifiedForced = this.observableOrDefault(null);
        this.externalIsModifiedForced = this.observableOrDefault(null);
        this.useShortTransactions = this.observableOrDefault(false);
        this.forceReadonly = this.observableOrDefault(false);
    }

    public setGenericObjectModified(isModified: boolean, force: boolean) {
        if (force)
            this.externalIsModifiedForced(isModified);

        this.genericObject.isModified(isModified);
    }

    private increaseBusyCounter(): void {
        if (this.isDisposed)
            return;

        if (this._isSummaryViewMode)
            return;

        if (!this.busyCounter)
            this.busyCounter = new BusyCounter(this.isBusy);

        this.busyCounter.increment();
    }

    private decreaseBusyCounter(): void {
        if (this.isDisposed)
            return;

        if (this._isSummaryViewMode)
            return;

        this.busyCounter.decrement();
    }

    public getContextParameters(): Object {
        return $.extend({}, this._contextParameters);
    }

    /**
     * Gibt den PDTypeName des Contexts zurück
     */
    public getPdTypeName(): string {
        return this._pdTypeName;
    }

    /**
     * Gibt den PDTypeName des konkreten Typen aus der DetailView zurück
     */
    public getConcretePdTypeName(): string {
        return this.detailViewContextDTO.detailView.typeName;
    }


    private updateContextParameters(contextParameters: any): void {
        this._contextParameters = contextParameters;
        if (contextParameters.hasOwnProperty('CALL_ENVELOPE_KEY')) {
            let call = FrontController.getCall(contextParameters['CALL_ENVELOPE_KEY']);
            if (call != null) {
                call.contextParameters = $.extend(true, {}, contextParameters);
            }
        }
    }

    /**
     * Erzeugt einen neuen Kontext im DetailViewContextStack des ViewModels
     * @param {DetailViewContext} caller - Der aufrufende Kontext
     * @param {string} typeName - Der Typ des zu erzeugenden Kontexts
     * @param {number} pdoId - Die Id des zu erzeugenden Kontexts
     * @param {string} relationName: Der Name der Assoaziation
     * @param {number} callId: CallId
     * @param {string} principalFilter - Der Filtermodus für Mandanten
     * @param {GenericObject} genericObject - Das GenericObject (Default: aus dem aktuellen Context)
     * @param {string} filter - Ein statischer Filter, falls zunächst eine Liste geöffnet wird
     * @returns {Deferred}
     */
    protected _createContext(caller: DetailViewContext | ControlFactory | ControlViewModel, typeName: string, pdoId: string, relationName: string, callId?: string,
        principalFilterMode?: string, genericObject?: GenericObject, filter?: string): JQueryPromise<any> {
        const gO: GenericObject = genericObject || this.genericObject;
        return $.when(this.updateProperties()).then((updatePropertiesSuccess) => {
            if (updatePropertiesSuccess !== false) {
                this.viewModel.createContext(caller, typeName, pdoId, gO.pdoId, gO.pdTypeName, relationName, null, callId, principalFilterMode, filter);
            }
        });
    }

    protected createContextModal(caller: DetailViewContext, typeName: string, pdoId: string, relationName: string, callId?: string,
        principalFilterMode?: string, genericObject?: GenericObject, filter?: string): JQueryPromise<any> {
        const gO: GenericObject = genericObject || this.genericObject;
        return $.when(this.updateProperties()).then((updatePropertiesSuccess) => {
            if (updatePropertiesSuccess !== false) {
                this.viewModel.createContextModal(caller, typeName, pdoId, gO.pdoId, gO.pdTypeName, relationName, null, callId);
            }
        });
    }

    /**
     * Verarbeitet ein vom Server erhaltenes ValidationDTO
     * @param {ValidationDTO} validationDTO
     */
    private handleValidationDTO(validationDTO: CoreFrameDTOs.IValidationDTO): any {
        if (validationDTO.status === 'Warning' && !validationDTO.isValid && _.keys(validationDTO.validationMessages).length > 0) {
            return this.convertValidationObject(validationDTO.validationMessages);
        }

        if (this.validateStatus(validationDTO)) {
            return validationDTO.isValid;
        }

        return validationDTO.isValid;
    }

    private removePropertyUpdatedByServer(name: string): void {
        this._updatedByServer = _.without(this._updatedByServer, name);
    }

    private removeChangesThatComesFromServer(pendingChanges: Object): Object {
        let newChanges = {};
        let pendingChangesKeys = _.keys(pendingChanges);

        // zusätzlich prüfen, ob sich Werte seit dem Update vom Server geändert haben
        _.intersection(pendingChangesKeys, this._updatedByServer).forEach((item) => {
            if (this._updatedByServerValues[item] !== pendingChanges[item]) {
                // wert wurde seit update vom server geändert
                this.removePropertyUpdatedByServer(item);
            }
            else {
                // wenn der wert immer noch der selber wie vom server ist,
                //committe um nicht mehr als geändert zu erscheinen 
                if (this.genericObject.values[item]) {
                    this.genericObject.values[item].commit();
                }
            }
        });

        // alle einträge filtern die erst vom server erhalten wurden
        _.difference(pendingChangesKeys, this._updatedByServer).forEach((item) => {
            newChanges[item] = pendingChanges[item];
        });

        return newChanges;
    }

    /**
     * Verarbeitet ein vom Server erhaltened OperationResultDTO
     * @param {OperationDTO} operation
     * @param {OperationResultDTO} operationResultDTO
     * @returns {bool}
     */
    private handleOperationResultDTO(operation: CoreFrameDTOs.IOperationDTO, operationResultDTO: CoreFrameDTOs.IOperationResultDTO): boolean {
        if (operationResultDTO.subject)
            this.genericObject.refresh(operationResultDTO.subject);

        const validationResult = this.handleValidationDTO(operationResultDTO);

        const resultIsBool = typeof validationResult == "boolean"
        if (resultIsBool && validationResult === false)
            return false;

        this.updateContextParameters(operationResultDTO.contextParameters);

        if (operationResultDTO.isModified === true) {
            this.genericObject.isModified(true);
            this.removeDefaultIterators().then(() => {
                Messenger.createMessageAndSend('RefreshAssociationsMessage', { context: this });
            });
        }

        if (!resultIsBool) {
            // Validierung fehlgeschlagen
            this.showValidationMessages(validationResult);
            return false;
        }

        if (operation.showResult && operationResultDTO.returnType != null) {
            const returnValue = operationResultDTO.returnValue || 'null';
            const message = returnValue + '';
            RADApplication.getInstance().showMessageBox(this.MessageBoxViewModel.createNoticeMessageBox(message));
        }

        return true;
    }

    /**
     * Ergänzt die nestedContextDTOs mit dem Flag isNestedContext, so lässt sich die redundante Fehlerausgabe verhindern
     */
    private tagNestedContextDTOs(detailViewContextDTO: CoreFrameDTOs.IDetailViewContextDTO): void {
        if (detailViewContextDTO && detailViewContextDTO.nestedDetailViewContexts) {
            let keys = _.allKeys(detailViewContextDTO.nestedDetailViewContexts);
            keys.forEach((key) => {
                let nestedContext = detailViewContextDTO.nestedDetailViewContexts[key];
                nestedContext.isNestedContext = true;
                this.tagNestedContextDTOs(nestedContext);
            });
        }
    }

    /**
     * Verarbeitet ein vom Server erhaltenes DetailViewContextDTO
     * @param {DetailViewContextDTO} detailViewContextDTO
     * @returns {bool}
     */
    private handleDetailViewContextDTO(detailViewContextDTO: CoreFrameDTOs.IDetailViewContextDTO): boolean {
        if (this.validateStatus(detailViewContextDTO, (detailViewContextDTO as any).isNestedContext !== true)) {
            this.detailViewContextDTO = detailViewContextDTO;

            return true;
        }

        return false;
    }

    /**
     * Ruft ein Objekt mit ausstehenden Änderungen des aktuellen GenericObjects ab,
     * welche nicht INVALIDE oder UNTERDRÜCKT sind
     * @return {object} - Key: Name des Properties, Value: Wert des Properties
     */
    private getPendingChanges(): Object {
        let changes = this.genericObject.changes();
        let invalidProperties = this.getInvalidProperties();
        let supressedProperties = this.getSupressedProperties();

        let pendingChanges = _.omit(changes, (value, key) => {
            return key in invalidProperties || supressedProperties.indexOf(key) > -1;
        });

        return this.removeChangesThatComesFromServer(pendingChanges);
    }

    /**
     * Ruft ein Objekt mit invaliden Properties ab,
     * welche CLIENTSEITIG validiert werden können
     * @return {object} - Key: Name des Properties, Value: Ergonomischer Bezeichner des Items
     */
    private getInvalidProperties(): Object {
        let invalid = {};

        this.getCreatedControlFactories().forEach((factory) => {
                // Customvalidierungen: Hier muss auf die Factories zugegriffen werden, da die Validierung implizit über Subscriptions stattfindet
                factory.callClientValidation();
                if (Utilities.isFunction(factory.isValid) && !factory.isValid())
                    invalid[factory.detailViewItem.name] = factory.getErgNameForValidationMessage();
            });

        return invalid;
    }

    /**
     * Ruft ein Array mit unterdrückten Properties ab
     * @return {[string]} - Liste mit Property-Namen
     */
    private getSupressedProperties(): Array<string> {
        let supressedProperties = this._supressedProperties;
        return supressedProperties;
    }

    /**
     * Ruft ein Objekt mit verschachtelten DetailViewContexts ab
     */
    private getNestedDetailViewContexts(): { [key: string]: DetailViewContext } {
        let nestedDetailViewContexts = this._nestedDetailViewContexts();
        return nestedDetailViewContexts;
    }

    /**
     * Ruft ein Objekt mit verschachtelten InlineEditingContexts ab
     */
    private getNestedInlineEditingContexts(): Array<EditContext> {
        let nestedInlineEditingContexts = this._nestedInlineEditingContexts();
        return nestedInlineEditingContexts;
    }

    /**
     * Ermittelt einen NestedDetailViewContext
     * @return {object} - NestedDetailViewContext
     */
    private findNestedDetailViewContext(name: string): DetailViewContext {
        let nestedDetailViewContexts = this._nestedDetailViewContexts();
        return nestedDetailViewContexts[name];
    }

    /**
     * Gibt an, ob der DetailViewContext ein NestedDetailViewContext ist
     */
    public isNestedDetailViewContext(): boolean {
        return !Utilities.isNullOrUndefinedOrBlank(this.__parent);
    }

    /**
     * Ermittelt die zu erzeugenden Items in einer Gruppe
     * @param {DetailViewGroupDTO} group
     * @param {bool} countthis
     * @returns {[string]} - Ein Array mit allen zu erzeugenden Items
     */
    private getItemsInGroup(group: CoreFrameDTOs.IDetailViewGroupDTO | CoreFrameDTOs.IDetailViewDTO, countthis: boolean): Array<string> {
        let result = [];
        let anyGroup = group as any;

        if (anyGroup != null) {
            if (countthis === true) {
                result.push(anyGroup.name);
            }

            if (anyGroup.detailViewGroups != null) {
                for (let i = 0; i < anyGroup.detailViewGroups.length; i++) {
                    result = _.union(result, this.getItemsInGroup(anyGroup.detailViewGroups[i], true));
                }
            }

            if (anyGroup.items != null) {
                for (let i = 0; i < anyGroup.items.length; i++) {
                    result = _.union(result, this.getItemsInGroup((anyGroup.items[i] as any), true));
                }
            }
        }

        return result;
    }

    /**
     * Collects all items names (groups, items)
     */
    public collectAllItems(): void {
        this._items = this.getItemsInGroup(this.detailViewContextDTO.detailView, false);
    }

    /**
     * Ruft eine Operation des DetailViewContexts ab
     * @param {string} name - Der Name der Operation
     * @returns {CoreFrameDTOs.IOperationDTO}
     */
    private getOperation(name: string): CoreFrameDTOs.IOperationDTO {
        let detailView = this.detailView();
        let result = null;

        for (let i = 0; i < detailView.operations.length; i++) {
            if (detailView.operations[i].name === name) {
                result = detailView.operations[i];
                break;
            }
        }

        if (result == null) {
            result = { name: name, showResult: false };
        }

        return result;
    }

    /**
     * Ermittelt die optimale Label-Breite aller erzeugten Factories
     * @returns {number}
     */
    private getLabelOptimalWidth(): number {
        let idealWidth = 0;
        let maxCharacters = 0;
        let maxText = '';
        let widthElement = $('<span>').appendTo('body');
        let dto = this.detailViewContextDTO;

        let ergNames = this.collectErgNamesForLabelWidth(dto.detailView);
        let translatedErgNames = [];

        ergNames.forEach((ergName) => {
            const translated = ResourceManager.getInstance().get(ergName);
            // Nur aufgelöste Namen hinzufügen, da sonst die Länge des ErgNames genutzt wird
            if (translated.toLowerCase().indexOf(ergName.toLowerCase()) != 0)
                translatedErgNames.push(translated);
        });

        let longestString = translatedErgNames.sort((a, b) => { return b.length - a.length; })[0];

        idealWidth = widthElement.text(longestString).outerWidth() + 22;
        widthElement.remove();

        return idealWidth + 22;
    }

    private collectErgNamesForLabelWidth(dvObject: CoreFrameDTOs.IDetailViewGroupDTO | CoreFrameDTOs.IDVGItemDTO | CoreFrameDTOs.IDetailViewDTO): Array<string> {
        let ergNames = [];

        // Ist eine Gruppe
        if (dvObject.hasOwnProperty("detailViewGroups")) {
            let group = dvObject as CoreFrameDTOs.IDetailViewGroupDTO;

            if (!Utilities.isNullOrUndefined(group.items)) {
                group.items.forEach((item) => {
                    ergNames = _.union(ergNames, this.collectErgNamesForLabelWidth(item));
                });
            }

            if (!Utilities.isNullOrUndefined(group.detailViewGroups)) {
                group.detailViewGroups.forEach((group) => {
                    ergNames = _.union(ergNames, this.collectErgNamesForLabelWidth(group));
                });
            }
        }

        // Ist ein Item
        if (dvObject.hasOwnProperty("children")) {
            let item = dvObject as CoreFrameDTOs.IDVGItemDTO;

            if (!Utilities.isNullOrUndefined(item) && !Utilities.isNullOrUndefined(item.ergName)) {
                if (this.isLabelWidthRelevant(item)) {
                    ergNames.push(item.ergName);
                }
            }

            if (!Utilities.isNullOrUndefined(item.children)) {
                item.children.forEach((child) => {
                    ergNames = _.union(ergNames, this.collectErgNamesForLabelWidth(child));
                });
            }
        }

        return ergNames;
    }

    private isLabelWidthRelevant(item: CoreFrameDTOs.IDVGItemDTO): boolean {
        if (!item.uiGenerator)
            return true;

        let uiGen = item.uiGenerator.toLowerCase();
        if (uiGen.indexOf("assoziationgrid") >= 0 && uiGen.indexOf("labelposition") == -1)
            return false; //Falls nicht spezifiziert
        //SAGGRC-1353:Top und None-Label werden nciht mehr berücksichtigt
        return uiGen.indexOf("labelposition=none") == -1 && uiGen.indexOf("labelposition=top") == -1;
    }

    /**
    * @override
    */
    protected showValidationMessages(validationMessages: IValidationMessage[], showMessageBox: boolean = true, withCancelButton: boolean = false): boolean {
        const hasValidationMessages = validationMessages.length > 0;

        if (!hasValidationMessages)
            return false;

        let sortedValidationMessages = this.sortValidationMessages(validationMessages);

        // Validierungsnachrichten für den aktuellen Kontext
        for (let i = 0; i < _.keys(sortedValidationMessages.currentContextValidationMessages).length; i++) {
            const currentMessage = sortedValidationMessages.currentContextValidationMessages[i];

            for (let j = 0; j < this.onValidationError.length; j++) {
                this.onValidationError[j](currentMessage.property, currentMessage.message, false);
            }
        }

        // Validierungsnachrichten für Nested-Contexts
        if (sortedValidationMessages.otherContextValidationMessages.length > 0) {
            const nestedDetailViewContexts = this.getNestedDetailViewContexts();
            for (let name in nestedDetailViewContexts) {
                if (!nestedDetailViewContexts.hasOwnProperty(name))
                    continue;
                const nestedDetailViewContext = nestedDetailViewContexts[name];
                nestedDetailViewContext.showValidationMessages(sortedValidationMessages.otherContextValidationMessages, false);
            }
        }

        if (showMessageBox) {
            const itemNames = this.extractItemNames(validationMessages);
            var messageBoxVm;
            if (itemNames.length === 0 || !itemNames) {
                var properties = validationMessages.map(x => x.property).join(',');
                var errorText = ResourceManager.getInstance().get('VALIDATIONERROR_HIDDEN_PROPERTY');
                messageBoxVm = this.MessageBoxViewModel.createErrorMessageBoxWithoutErgnames(errorText + properties);
            } else {
                messageBoxVm = this.MessageBoxViewModel.createValidationMessageBox(itemNames);

                if (withCancelButton) {
                    const cancelButton = this.createCancelButton(() => {
                        messageBoxVm.window.isOpen(false);
                        this.cancel();
                    });

                    messageBoxVm.buttons.push(cancelButton);
                }
            }

            RADApplication.getInstance().showMessageBox(messageBoxVm);
        }

        return true;
    }

    private createCancelButton(callback: Function) {
        return new ButtonViewModelBuilder()
            .withLabel()
            .withErgName('CLOSE_DISCARD_CHANGES')
            .done()
            .withImage()
            .withSource('cancel_off.png')
            .withAlternativeTextErgName('CLOSE_DISCARD_CHANGES')
            .done()
            .withOnClick(() => {
                callback();
            })
            .build();
    }

    private sortValidationMessages(validationMessages: IValidationMessage[]): ISortedValidationMessages {
        const validationMessagesForCurrentContext = validationMessages.filter(v => v.context === this);
        const validationMessagesForNestedContexts = validationMessages.filter(v => v.context !== this);

        return {
            currentContextValidationMessages: validationMessagesForCurrentContext,
            otherContextValidationMessages: validationMessagesForNestedContexts
        };
    }



    /**
     * Schließt den aktuellen Kontext und navigiert zur vorherigen View
     * @returns {Deferred}
     */
    protected closeCurrentContext(): JQueryPromise<boolean> {
        this.increaseBusyCounter();

        let deferred = $.Deferred();

        const stack = NavigationService.getInstance().detailViewStack();
        const isNewAndCanceled = this.action == 'CREATE' && this.getReasonForLeaving() == 'Cancel';

        if (stack.length == 1 && !isNewAndCanceled) {
            const pdoId = this.genericObject.pdoId;
            const listView = NavigationService.getInstance().findLastFactory('ListView') as ListViewViewFactory;

            if (listView && listView.viewModel) {
                const lvTypeName = listView.viewModel.listView()?.typeName;
                if (lvTypeName === this._pdTypeName) {
                    listView.viewModel.setInitialSelected(pdoId);
                }
                else {
                    // reinitialize the listview
                    if (listView.viewModel.selectedPDO)
                        listView.viewModel.setInitialSelected(listView.viewModel.selectedPDO);
                }
            }
        }

        this.viewModel.removeCurrentContext((currentContext) => {
            this.decreaseBusyCounter();

            if (currentContext == null && !Config.detailView.ShowNoLastSavedViewAfterDVC && !this._hasPendingDVC) {
                NavigationService.getInstance().forceNavigateBack();
            }
            deferred.resolve(true);
        });

        return deferred.promise();
    }

    /**
     * Erzeugt eine UIListener-Instanz anhand des Alias im DetailViewContextDTO
     * @returns {UIListener}
     */
    private createUIListener(): UIListener {
        let alias = this.detailViewContextDTO.detailView.uiListener;
        return UIListener.createInstance(alias);
    }

    private manageModified(): KnockoutObservable<boolean> {
        // Dient nur zur Neuberechnung des computeds.
        const trigger = this.observableOrDefault(false).extend({ notify: 'always' });
        return this.defineComputed({
            read: () => {
                trigger();

                // modified bei short transaction im Edit Modus wird gesondert behandelt, da hier meist keine Modifizierung vorliegen darf 
                if (!this.detailViewContextDTO.isNewObject && this.useShortTransactions()) {
                    const externalIsModifiedForced = this.externalIsModifiedForced();
                    if (externalIsModifiedForced)
                        return true;

                    const internalIsModifiedForced = this.internalIsModifiedForced();
                    if (internalIsModifiedForced)
                        return true;

                    return false;
                }

                return this.detailViewContextDTO.subject.isModified;
            },
            write: (value: boolean) => {
                this.detailViewContextDTO.subject.isModified = value;
                trigger(true);
            }
        });
    }

    /**
     * Erzeugt eine GenericObject-Instanz
     * @returns {GenericObject}
     */
    private createGenericObject(): GenericObject {
        let genericObject = new GenericObject(this.detailViewContextDTO.subject, undefined, this.manageModified());

        genericObject.onChange((property) => {
            this.uiListener['onPropertyValueUpdatedInternal'](this, property);
            this.uiListener.onGenericObjectUpdated(this, genericObject.changes());
            if (this.useShortTransactions())
                this.internalIsModifiedForced(true);
        }, null, this);

        //Wenn ContextParameter vorhanden sind, dann wird eine Validierung erzungen!
        if (this.detailViewContextDTO.isNewObject || Object.keys(this.getContextParameters()).length > 1) {
            genericObject.isModified(true);
        }

        if (!this._isSummaryViewMode) {
            genericObject.onDelayedChange(() => {
                const dv = this.detailView();
                if (dv.autoUpdateDerivedProperties !== false) {
                    this._updateProperties();
                }
            }, this);
        }

        return genericObject;
    }

    /**
     * Verwaltet den Lock-Status
     * Dieser kann nur auf true gesetzt werden. Ein Zurücksetzen ist nich möglich!
     */
    private manageLockStatus(): KnockoutComputed<boolean> {
        const obs = this.observableOrDefault(false);
        return this.defineComputed({
            read: () => {
                return obs();
            },
            write: (value: boolean) => {
                if (!value || obs() === value)
                    return;
                obs(value);
                _.values(this.getNestedDetailViewContexts()).forEach(c => c.isLocked(true));
                const parent = this.getParentContext();
                if (!Utilities.isNullOrUndefined(parent))
                    parent.isLocked(true);
            }
        });
    }

    /**
    * Verwaltet den Status des GenericObject und gibt ab, ob dieses modifiziert wurde
    */
    private manageModificationStatus(): KnockoutComputed<boolean> {
        return this.defineComputed(() => {
            const result = this.getModificationStatus();
            return result;
        });
    }

    private getModificationStatus() {
        let result = this.genericObject.isModified();

        let nestedContexts = this.getNestedDetailViewContexts();
        for (let name in nestedContexts) {
            if (nestedContexts.hasOwnProperty(name)) {
                if (nestedContexts[name].getModificationStatus())
                    result = true;
            }
        }

        return result;
    }

    /**
     * Verwaltet den Status des GenericObject und gibt ab, ob dieses ausstehende Änderungen hat
     */
    private managePendingModifications(): KnockoutComputed<boolean> {
        return this.defineComputed(() => {
            return this.hasPendingModifications();
        });
    }

    /**
     * @override
     * @inheritdoc 
     */
    public hasPendingModifications(checkNestedContexts: boolean = true, checkInlineContexts: boolean = true): boolean {
        let result = this.genericObject.hasPendingModifications();
        let nestedContexts = checkNestedContexts ? this.getNestedDetailViewContexts() : [];
        let nestedInlineEditingContexts = checkInlineContexts ? this.getNestedInlineEditingContexts() : [];

        for (let name in nestedContexts) {
            if (!nestedContexts.hasOwnProperty(name))
                continue;
            if (nestedContexts[name].hasPendingModifications()) {
                result = true;
            }
        }

        for (let contextKey in nestedInlineEditingContexts) {
            if (!nestedInlineEditingContexts.hasOwnProperty(contextKey))
                continue;
            const context = nestedInlineEditingContexts[contextKey];

            if (context.hasPendingModifications()) {
                result = true;
            }
        }

        return result;
    }

    /**
     * Ermittelt die optimale Breite der LabelColumn und setzt diese, falls sie nicht explizit definiert ist
     */
    private manageLabelColumnWidth(): void {
        let labelColumnWidth = this.labelColumnWidth();

        if (labelColumnWidth == null) {
            let width = this.getLabelOptimalWidth();

            let minWidth = RADApplication.getInstance().getSystemParam('Gui.MinLabelWidth', parseInt) || 200;
            let maxWidth = RADApplication.getInstance().getSystemParam('Gui.MaxLabelWidth', parseInt) || 240;

            if (width < minWidth) {
                width = minWidth;
            }

            if (width > maxWidth) {
                width = maxWidth;
            }

            this.labelColumnWidth(width);
        }
    }

    /**
     * Gibt an, ob alle Items des Kontexts erzeugt und geladen wurden
     * @returns {bool}
     */
    private isAllItemsCreated(): boolean {
        let createdItems = this._createdItems();
        let allItems = this._items;

        return _.difference(allItems, _.keys(createdItems)).length === 0;
    }

    /**
     * Gibt an, ob alle NestedContexts als bereit gelten
     */
    private areAllNestedContextsReady(): boolean {
        let result = true;
        if (this.isNestedDetailViewContext())
            result = this.detailView() != null;

        const nestedContexts = this.getNestedDetailViewContexts();
        // Wenn die NestedDetailViewContext noch nicht erzeugt wurden, ist die Menge leer
        // this.hasNestedDetailViewContexts entnimmt die Informationen aber bereits aus dem DTO 
        // und weiß vorher, ob es welche gibt
        if (_.keys(nestedContexts).length === 0 && this.hasNestedDetailViewContexts())
            result = false;

        for (const name in nestedContexts) {
            if (!nestedContexts.hasOwnProperty(name))
                continue;

			const nestedContext = nestedContexts[name];
			if (nestedContext == null || nestedContext.isDisposed)
				continue;

            if (!nestedContext.isDetailViewReady())
                result = false;
        }

        return result;
    }

    /**
     * Verwaltet den Status und gibt an, ob die DetailView geladen wurde
     */
    private createDetailViewReadyComputed(): KnockoutComputed<boolean> {
        return this.defineComputed(() => {
            const condition1 = this.detailView() != null;
            const condition2 = this.isAllItemsCreated();
            const condition3 = this.areAllNestedContextsReady();
            return condition1 && condition2 && condition3;
        });
    }

    private setupReadyHook() {
        this.subscribeUntil(this.isDetailViewReady, ready => {
            if (this.isDisposedOrDisposing())
                return true;

            if (ready)
                this.triggerReadyHook();

            return ready;
        });
    }

    private triggerReadyHook(): void {
        if (this.isSummaryViewMode()) {
            this.notifySummaryViewReady();
        } else {
            this.uiListener.onAfterRefresh(this); // TODO: Prüfen, ob das an dieser Stelle nötig ist
            this.notifyDetailViewReady();
        }
    }

    /**
     * Registriert den aktuellen DetailViewContext als NestedContext im Caller
     * @param {string} nestedContextName - Der Name des NestedContexts
     * @param {DetailViewContext} parent - Der aufrufende Kontext
     */
    private registerNestedContext(nestedContextName: string, parent: DetailViewContext): void {
        if (nestedContextName && parent) {
            let nestedDetailViewContexts = parent._nestedDetailViewContexts();

            nestedDetailViewContexts[nestedContextName] = this;
            parent._nestedDetailViewContexts.valueHasMutated();
        }

        this._nestedDetailViewContextDTOs = this.detailViewContextDTO.nestedDetailViewContexts as any;
        this._nestedDetailViewContextDTOs['_findByName'] = (name) => {
            for (let prop in this._nestedDetailViewContextDTOs) {
                let propName = prop.split('.').pop();

                if (propName === name) {
                    return this._nestedDetailViewContextDTOs[prop];
                }
            }

            return null;
        };
    }

    /**
     * Fügt einen InlineEditingContext dem aktuellen DetailViewContext hinzu
     */
    public registerNestedInlineEditingContext(inlineEditingContext: EditContext): void {
        this._nestedInlineEditingContexts.push(inlineEditingContext);
    }

    /**
     * Fügt einen InlineEditingContext dem aktuellen DetailViewContext hinzu
     */
    public removeNestedInlineEditingContext(inlineEditingContext: EditContext): void {
        this._nestedInlineEditingContexts.remove(inlineEditingContext);
    }

    /**
    * Erzeugt ein neues ToolBarViewModel
    * @returns {ToolBarViewModel}
    */
    public createToolBar(detailViewContext: CoreFrameDTOs.IDetailViewContextDTO): ToolBarViewModelType {
        if (this.isSummaryViewMode()) {
            return new this.ToolBarViewModel({
                parentLocator: this.detailViewContextDTO.detailView.typeName,
                locator: "toolbar"
            });
        }

        return new this.ToolBarViewModel({
            parentLocator: detailViewContext.detailView.typeName,
            locator: "toolbar",
            detectOverflowOnSecondaryItems: true,
            primary: this.createPrimaryToolBarLinks(detailViewContext),
            secondary: this.createSecondaryToolBarLinks(detailViewContext),
            extended: this.createExtendedToolBarLinks(detailViewContext)
        });
    }

    private canAddExclusiveLockLink() {
        const value = RADApplication.getInstance().getSystemParam('Gui.HideExclusiveLockButton') as string;
        if (!value)
            return true;

        return value.toLowerCase() !== 'true';
    }

    protected createPrimaryToolBarLinks(detailViewContext: CoreFrameDTOs.IDetailViewContextDTO): LinkViewModel[] {
        let linkViewModels = [];

        const useShortTransactions = detailViewContext.detailView?.useShortTransactions === true;
        if (useShortTransactions) {
            if (detailViewContext.isNewObject) {
                linkViewModels.push(this.createSaveLink(detailViewContext));
                linkViewModels.push(this.createCancelLink(detailViewContext));
            }
            else {
                linkViewModels.push(this.createCloseLink(detailViewContext));
                if (this.canAddExclusiveLockLink()) {
                    linkViewModels.push(this.createExclusiveLockLink(detailViewContext));
                }
            }
        }
        else {

            linkViewModels.push(this.createSaveLink(detailViewContext));
            linkViewModels.push(this.createCancelLink(detailViewContext));
            linkViewModels.push(this.createApplyLink(detailViewContext));
        }

        linkViewModels.push({});

        return linkViewModels;
    }

    private createCloseLink(detailViewContext: CoreFrameDTOs.IDetailViewContextDTO): LinkViewModel {
        return new this.LinkViewModel({
            parentLocator: detailViewContext.detailView.typeName,
            locator: "toolbar.close",
            label: new this.LabelViewModel({
                ergName: 'CLOSE',
                parentLocator: detailViewContext.detailView.typeName,
                locator: "toolbar.close.label",
            }),
            image: new this.ImageViewModel({
                source: 'selection_apply_off.png',
                parentLocator: detailViewContext.detailView.typeName,
                locator: "toolbar.close.image",
                alternativeText: new this.LabelViewModel({
                    ergName: 'CLOSE_TOOLTIP'
                })
            }),
            tooltip: new this.LabelViewModel({
                ergName: 'CLOSE_TOOLTIP'
            }),
            isActive: false,
            isVisible: true,
            isEnabled: true,
            onClick: () => {
                // Vorher den Focus verlassen, um sicher zu stellen, dass alle Inputs den Focus verloren haben und die Änderung übernommen haben.
                $(document.activeElement).blur();

                if (detailViewContext.lockAquired && detailViewContext.hasWriteRight)
                    this.saveInternal(!this.detailViewContextDTO.isNewObject);
                else
                    this.cancel();
            },
            hotkey: 'alt+s'
        })
    }

    private createExclusiveLockLink(detailViewContext: CoreFrameDTOs.IDetailViewContextDTO): LinkViewModel {
        return new this.LinkViewModel({
            parentLocator: detailViewContext.detailView.typeName,
            locator: "toolbar.exclusive_lock",
            label: new this.LabelViewModel({
                ergName: 'EXCLUSIVE_LOCK',
                parentLocator: detailViewContext.detailView.typeName,
                locator: "toolbar.exclusive_lock.label",
            }),
            image: new this.ImageViewModel({
                source: 'lock.png',
                parentLocator: detailViewContext.detailView.typeName,
                locator: "toolbar.exclusive_lock.image",
                alternativeText: new this.LabelViewModel({
                    ergName: 'EXCLUSIVE_LOCK_TOOLTIP'
                })
            }),
            tooltip: new this.LabelViewModel({
                ergName: 'EXCLUSIVE_LOCK_TOOLTIP'
            }),
            isActive: false,
            isVisible: true,
            isEnabled: this.defineComputed(() => {
                return detailViewContext.lockAquired && detailViewContext.hasWriteRight;
            }),
            onClick: () => {
                // Vorher den Focus verlassen, um sicher zu stellen, dass alle Inputs den Focus verloren haben und die Änderung übernommen haben.
                $(document.activeElement).blur();
                this.lockExclusive();
            },
        })
    }

    private createSaveLink(detailViewContext: CoreFrameDTOs.IDetailViewContextDTO): LinkViewModel {
        return new this.LinkViewModel({
            technicalName: Constants.TOOLBAR_SAVE_BUTTON,
            parentLocator: detailViewContext.detailView.typeName,
            locator: "toolbar.save",
            label: !Utilities.isMobileView() ? new this.LabelViewModel({
                ergName: 'SAVE',
                parentLocator: detailViewContext.detailView.typeName,
                locator: "toolbar.save.label",
            }) : null,
            image: new this.ImageViewModel({
                source: Config.inlineImages.TOOLBAR_SAVE,
                parentLocator: detailViewContext.detailView.typeName,
                locator: "toolbar.save.image",
                alternativeText: new this.LabelViewModel({
                    ergName: 'SAVE_TOOLTIP'
                })
            }),
            tooltip: new this.LabelViewModel({
                ergName: 'SAVE_TOOLTIP'
            }),
            isActive: false,
            isVisible: true,
            isEnabled: this.defineComputed(() => {
                return detailViewContext.lockAquired && detailViewContext.hasWriteRight && !this.forceReadonly();
            }),
            onClick: () => {
                // Vorher den Focus verlassen, um sicher zu stellen, dass alle Inputs den Focus verloren haben und die Änderung übernommen haben.
                $(document.activeElement).blur();
                this.save();
            },
            hotkey: 'alt+s'
        })
    }

    private createCancelLink(detailViewContext: CoreFrameDTOs.IDetailViewContextDTO): LinkViewModel {
        return new this.LinkViewModel({
            technicalName: Constants.TOOLBAR_CANCEL_BUTTON,
            parentLocator: detailViewContext.detailView.typeName,
            locator: "toolbar.cancel",
            label: !Utilities.isMobileView() ? new this.LabelViewModel({
                ergName: 'CANCEL',
                parentLocator: detailViewContext.detailView.typeName,
                locator: "toolbar.cancel.label",
            }) : null,
            image: new this.ImageViewModel({
                source: Config.inlineImages.TOOLBAR_ABORT,
                parentLocator: detailViewContext.detailView.typeName,
                locator: "toolbar.cancel.image",
                alternativeText: new this.LabelViewModel({
                    ergName: 'CANCEL_TOOLTIP'
                })
            }),
            tooltip: new this.LabelViewModel({
                ergName: 'CANCEL_TOOLTIP'
            }),
            isActive: false,
            isVisible: true,
            isEnabled: true,
            onClick: () => {
                // Vorher den Focus verlassen, um sicher zu stellen, dass alle Inputs den Focus verloren haben und die Änderung übernommen haben.
                $(document.activeElement).blur();
                this.cancel();
            }
        })
    }

    private createApplyLink(detailViewContext: CoreFrameDTOs.IDetailViewContextDTO): LinkViewModel {
        return new this.LinkViewModel({
            technicalName: Constants.TOOLBAR_APPLY_BUTTON,
            parentLocator: detailViewContext.detailView.typeName,
            locator: "toolbar.apply",
            label: !Utilities.isMobileView() ? new this.LabelViewModel({
                ergName: 'APPLY',
                parentLocator: detailViewContext.detailView.typeName,
                locator: "toolbar.apply.label",
            }) : null,
            image: new this.ImageViewModel({
                source: Config.inlineImages.TOOLBAR_APPLY,
                parentLocator: detailViewContext.detailView.typeName,
                locator: "toolbar.apply.image",
                alternativeText: new this.LabelViewModel({
                    ergName: 'APPLY_TOOLTIP'
                })
            }),
            tooltip: new this.LabelViewModel({
                ergName: 'APPLY_TOOLTIP'
            }),
            isActive: false,
            isVisible: true,
            isEnabled: this.defineComputed(() => {
                return detailViewContext.lockAquired && detailViewContext.hasWriteRight && this._hasPendingModifications() === true && !this.forceReadonly();
            }),
            onClick: () => {
                // Vorher den Focus verlassen, um sicher zu stellen, dass alle Inputs den Focus verloren haben und die Änderung übernommen haben.
                $(document.activeElement).blur();
                this.apply();
            }
        })
    }

    protected createSecondaryToolBarLinks(detailViewContext: CoreFrameDTOs.IDetailViewContextDTO): LinkViewModel[] {
        let linkViewModels = [];

        for (let i = 0; i < detailViewContext.detailView.operations.length; i++) {
            let lvm = this.createOperationLinkViewModel(detailViewContext, detailViewContext.detailView.operations[i]);
            linkViewModels.push(lvm);
        }

        if (_.keys(this._nestedDetailViewContextDTOs).length > 1) {
            for (let nestedContextName in this._nestedDetailViewContextDTOs) {
                if (nestedContextName !== '_findByName') {
                    for (let k = 0; k < this._nestedDetailViewContextDTOs[nestedContextName].detailView.operations.length; k++) {
                        let lvm = this.createOperationLinkViewModel(detailViewContext, this._nestedDetailViewContextDTOs[nestedContextName].detailView.operations[k], nestedContextName);
                        linkViewModels.push(lvm);
                    }
                }
            }
        }

        this.addDetailViewMetadataLink(linkViewModels, detailViewContext);

        return linkViewModels;
    }

    protected createExtendedToolBarLinks(detailViewContext: CoreFrameDTOs.IDetailViewContextDTO): LinkViewModel[] {
        let linkViewModels = [];

        if (RADApplication.getInstance().isSystemParamSetAndTrue('Gui.ShowFavourites')) {
            linkViewModels.push(new this.LinkViewModel({
                tooltip: new this.LabelViewModel({
                    ergName: 'FAVOURITE_TOOLTIP'
                }),
                image: new this.ImageViewModel({
                    source: this.defineComputed(() => {
                        let favorites = RADApplication.getInstance().user.getUserSpecificData('RAD_FAVOURITESITES', {})();
                        let address = NavigationService.getInstance().getAddress().toString();

                        const urlPrefix = '$$TEMPLATE_HANDLER$$?TemplateName=$$TEMPLATE_STYLE$$&ImageName';
                        if (favorites != null && address in favorites) {
                            return `${urlPrefix}=${GlobalConfiguration.inlineImages.FAVOURITE_ACTIVE}`;
                        } else {
                            return `${urlPrefix}=${GlobalConfiguration.inlineImages.FAVOURITE_INACTIVE}`;
                        }
                    }),
                    alternativeText: new this.LabelViewModel({
                        ergName: 'FAVOURITE_TITLE'
                    })
                }),
                isActive: false,
                isVisible: true,
                isEnabled: true,
                parentLocator: this.detailViewContextDTO.detailView.typeName,
                locator: "toolbar.favourites",
            }));
        }

        return linkViewModels;
    }

    private createOperationLinkViewModel(detailViewContext: CoreFrameDTOs.IDetailViewContextDTO, operation: CoreFrameDTOs.IOperationDTO, nestedContextName: string = ""): LinkViewModelType {

        let locator = "toolbar.operation." + operation.name;

        let linkViewModel = new this.LinkViewModel({
            parentLocator: this.detailViewContextDTO.detailView.typeName,
            locator: locator,
            label: new this.LabelViewModel({
                ergName: operation.ergName,
                parentLocator: this.detailViewContextDTO.detailView.typeName,
                locator: locator + ".label",
            }),
            image: new this.ImageViewModel({
                source: operation.iconUrl,
                parentLocator: this.detailViewContextDTO.detailView.typeName,
                locator: locator + ".image",
                alternativeText: new this.LabelViewModel({
                    ergName: operation.ergName ?? ''
                })
            }),
            isActive: false,
            isVisible: (operation as any).isVisible == undefined ? true : (operation as any).isVisible,
            isEnabled: this.defineComputed(() => {
                let isEnabled = (operation as any).isEnabled == undefined ? true : (operation as any).isEnabled;
                return detailViewContext.lockAquired === true && operation.hasRight === true && isEnabled;
            }),
            tooltip: operation.tooltip != null && operation.tooltip != '' ? new this.LabelViewModel({
                ergName: operation.tooltip
            }) : null,
            technicalName: operation.name,
            onClick: () => {
                if (nestedContextName) {
                    let ndvcontext = this.findNestedDetailViewContext(nestedContextName);
                    ndvcontext.callInterceptableOperation(operation.name, []);
                } else {
                    this.callInterceptableOperation(operation.name, []);
                }
            }
        });


        return linkViewModel;
    }

    public addDetailViewMetadataLink(linkViewModels: Array<LinkViewModelType>, detailViewContext: CoreFrameDTOs.IDetailViewContextDTO): void {
        linkViewModels.push(new this.LinkViewModel({
            label: new this.LabelViewModel({
                ergName: 'EDIT_METADATA_BUTTON_TEXT'
            }),
            image: new this.ImageViewModel({
                source: Config.inlineImages.GEAR,
                alternativeText: new this.LabelViewModel({
                    ergName: 'EDIT_METADATA_BUTTON_TEXT'
                })
            }),
            isActive: false,
            isVisible: this.defineComputed(() => {
                // Nur SuperUser darf Metadaten bearbeiten
                if (!RADApplication.getInstance().user.isSuperUser()) {
                    return false;
                }

                let dv = detailViewContext.detailView;
                let subject = detailViewContext.subject;

                // Bearbeiten wir schon die Metadaten der DV für Metadaten, so würde dieser Button nur wieder die gleiche Maske öffnen,
                // was aufgrund von Locking zu einem Fehler führt
                if (dv != null && subject != null &&
                    dv.typeName == 'RAD.RADFoundation.ProblemDomain.Meta.DetailView' &&
                    dv.detailViewID == subject.pdoId) {
                    return false;
                }

                return true;
            }),
            isEnabled: true,
            onClick: () => {
                let pdTypeName = 'RAD.RADFoundation.ProblemDomain.Meta.DetailView';
                let pdoId = detailViewContext.detailView.detailViewID;

                this.viewModel.createContext(null, pdTypeName, pdoId);
            }
        }));
    }

    /**
     * Liefert das für diesen Kontext gültige DetailViewContextDTO
     * @returns {JQueryPromis<any>}
     */
    private loadDetailViewContextDTO(): JQueryPromise<any> {
        let deferredOrDTO;

        if (this._detailViewContextDTO != null) {
            deferredOrDTO = this._detailViewContextDTO;
        } else if (!Utilities.isNullOrUndefinedOrBlank(this._contextRelationName) && (!Utilities.isNumeric(this._pdoId) || parseInt(this._pdoId) < 0)) {
            deferredOrDTO = RADEnvironmentProxy.getInstance().createDetailViewContextAndConnectNew({
                typeName: this._pdTypeName,
                contextPDOID: this._contextPdoId,
                contextTypeName: this._contextPdTypeName,
                contextRelationName: this._contextRelationName,
                contextParameters: this.getContextParameters()
            });
        } else {
            deferredOrDTO = RADEnvironmentProxy.getInstance().createDetailViewContext({
                typeName: this._pdTypeName,
                pdoID: this._pdoId,
                acquireLock: true,
                contextTypeName: this._contextPdTypeName,
                contextRelationName: this._contextRelationName,
                contextParameters: this.getContextParameters()
            });
        }

        /*
         * $.when akzeptiert sowohl Deferreds, als auch nicht-Deferreds als Parameter
         * und übergibt die ausgewerteten Werte an seine Listener
         */
        return $.when(deferredOrDTO);
    }

    /**
     * Initialisiert den DetailViewContext
     * @returns {Deferred}
     */
    protected initDetailViewContext(): JQueryPromise<any> {
        this.increaseBusyCounter();

        return this.loadDetailViewContextDTO().then((detailViewContextDTO: CoreFrameDTOs.IDetailViewContextDTO) => {
            if (this.isDisposed)
                return null;
            this.tagNestedContextDTOs(detailViewContextDTO);
            return this.handleDetailViewContextDTO(detailViewContextDTO);
        }).then((contextLoadSuccess) => {

            if (this.isDisposed)
                return;

            if (this.checkDispose()) {
                this.dispose();
                return;
            }

            if (contextLoadSuccess !== false) {
                this.id = this.detailViewContextDTO.id;

                this.uiListener = this.createUIListener();

                this.genericObject = this.createGenericObject();
                this.notifyRefineContextDto();

                this.defaultIterators = this.detailViewContextDTO.assoziationGridIterators;

                this.publicUserData = this.detailViewContextDTO.publicUserData;

                this._callId = this._callId || this.viewModel.viewFactory?.contextID;

                this._isModified = this.manageModificationStatus();

                this.isLocked((this.detailViewContextDTO.lockedByUser != null && this.detailViewContextDTO.lockedByUser.length > 0) || !this.detailViewContextDTO.hasWriteRight);

                this._hasPendingModifications = this.managePendingModifications();

                this.manageLabelColumnWidth();

                this.hasNestedDetailViewContexts(_.keys(this.detailViewContextDTO.nestedDetailViewContexts).length > 0);

                this.isDetailViewReady = this.createDetailViewReadyComputed();
                this.setupReadyHook();

                this.registerNestedContext(this._nestedContextName, this.__parent as any);

                this.toolBar = this.createToolBar(this.detailViewContextDTO);

                this.notifyBeforeRender();

                this.collectAllItems();

                this.updatePageTitle(this.detailViewContextDTO);

                this.detailView(this.detailViewContextDTO.detailView);

                if (this.detailViewContextDTO.detailView)
                    this.useShortTransactions(this.detailViewContextDTO.detailView.useShortTransactions);

                this.notifyDetailViewEntered(false);

                this.raiseChangeHeader();

                this.registerForChipsChangedMessage();

                // notifyDetailViewReady wird automatisch aufgerufen, sobald alles geladen wurde
                this._isInit(true);
            }
            else {
                this.onDetailViewEnterFailed();
            }
        }).always(() => {
            this.decreaseBusyCounter();
        }).promise();
    }

    private registerForChipsChangedMessage(): void {
        Messenger.registerForMessageId('FilterChipsChanged', this.applyMessage.bind(this), this);
    }

    private applyMessage(message: FilterChipsChangedMessage): void {
        if (this.isDisposed || !message.isPublicChip)
            return;

        let factoryList = this.getCreatedControlFactories().filter(x => x.detailViewItem?.listView?.listViewID == message.listViewPdoId);

        if (factoryList.length != 1)
            return;

        let grid = (factoryList[0] as any).dataGrid;
        if (Utilities.isNullOrUndefined(grid))
            return;

        let publicData = grid.filter?.filterChipsViewModel?.publicUserData();
        if (Utilities.isNullOrUndefined(publicData))
            return;

        this.detailViewContextDTO.publicUserData[`LV: ${grid.pdTypeName}`] = JSON.stringify(publicData);
    }

    protected checkDispose(): boolean {
        return this.viewModel == null || this.viewModel.isDisposed || this.viewModel.viewFactory == null || this.viewModel.viewFactory.isDisposed;
    }

    /**
     * Wird ausgeführt, wenn die Initalisierung der DetailView fehlgeschlagen ist
     */
    protected onDetailViewEnterFailed(): void {
        this.notifyDetailViewEntered(true);
        const stack = this.viewModel.detailViewContextStack();
        // Wenn der Stack leer ist, wurde initial zu einer DV navigiert, hier muss dann zurücknavgiert werden
        // Navigieren zu einer Assoziation erfordert keine Zurücknavigation, da hier der DetailViewContextStack
        // maßgebend ist, welche DV angezeigt wird, d.h. es wird kein Context dem Stack hinzugefügt (siehe DetailViewViewModel)
        if (stack.length === 0)
            NavigationService.getInstance().forceNavigateBack();
    }

    /**
     * Setzt ausstehende Änderungen des Kontexts zurück
     */
    public _revertPendingChanges(): boolean {
        this.genericObject.revert();
        this.genericObject.isModified(false);

        let nestedContexts = this.getNestedDetailViewContexts();
        for (let name in nestedContexts) {
            nestedContexts[name]._revertPendingChanges();
        }

        return true;
    }

    /**
    * Speichert den DetailViewContext
    * @returns {Deferred}
    */
    private _applyDetailViewContext(evaluateProperties: boolean): JQueryPromise<boolean | IValidationMessage[]> {
        if (this !== this.getRootDetailViewContext())
            return this.getRootDetailViewContext()._applyDetailViewContext(evaluateProperties);

        return RADEnvironmentProxy.getInstance().applyDetailViewContext({
            detailViewContextID: this.id,
            values: JSON.stringify({})
        }).then((validationDTO) => {
            return this.handleValidationDTO(validationDTO);
        }).then((result) => {
            if (result !== true) {
                return result;
            }

            if (this.useShortTransactions())
                this.switchToShortTransactionUpdateMode();

            this._hasObjectChanged = true;
            this.resetGenericObjectsModifiedStatus();

            if (evaluateProperties !== false) {
                return this.forceUpdateProperties().then((result) => {
                    Messenger.createMessageAndSend('DetailViewContextAppliedMessage', { context: this });
                    // Zurücksetzen, damit anschließende Änderungen nach Klick auf "Übernehmen" auch isModified auf true setzen
                    this.resetForcedUpdate();
                    return result;
                });
            }

            return result;
        }).promise();
    }

    /**
     * @override
     * @inheritdoc 
     */
    protected resetGenericObjectsModifiedStatus(): void {
        if (this !== this.getRootDetailViewContext()) {
            this.getRootDetailViewContext().resetGenericObjectsModifiedStatus();
            return;
        }

        super.resetGenericObjectsModifiedStatus();
    }

    /**
     * @override
     * @inheritdoc
     */
    protected resetGenericObjectsModifiedStatusInternal(): void {
        super.resetGenericObjectsModifiedStatusInternal();

        const nestedContexts = this.getNestedDetailViewContexts();
        for (const key of Object.keys(nestedContexts)) {
            nestedContexts[key].resetGenericObjectsModifiedStatusInternal();
        }

        const nestedInlineEditingContexts = this.getNestedInlineEditingContexts();
        for (const context of nestedInlineEditingContexts) {
            // Muss leider zu any gecastet werden, da protected
            // Durch Einbindung der .d.ts-Files wird die Vererbung nicht erkannt
            (context as any).resetGenericObjectsModifiedStatusInternal();
        }
    }

    /**
     * Sendet ausstehende Wert-Änderungen an den Server
     * @param {[string]} propertyNames - Eine OPTIONALE Liste der Namen der zu übermittelnden Properties
     * @returns {Deferred}
     */
    protected _updateProperties(propertyNames?: Array<string>): JQueryPromise<boolean> {
        // In einer SummaryView sollen keine Änderungen zum Server geschickt werden
        if (this.isSummaryViewMode())
            return Utilities.resolveImmediately(true);

        let pendingChanges = this.getPendingChanges();

        if (propertyNames) {
            pendingChanges = _.pick(pendingChanges, (value, key) => {
                return propertyNames.indexOf(key) > -1;
            });
        }

        let hasPendingChanges = _.keys(pendingChanges).length > 0 || this.isForcedUpdate();
        if (!hasPendingChanges)
            return Utilities.resolveImmediately(true);

        return RADEnvironmentProxy.getInstance().updateProperties({
            pdoID: this.genericObject.pdoId,
            values: JSON.stringify(pendingChanges),
            scopedProperties: [],
            nestedContextName: this.getNestedContextPath()
        }).then((updateDTO) => {
            if (this.isDisposed)
                return false;

            const result = this.handleUpdateDTO(updateDTO);
            if (!result.isSuccessful) {
                const currentContext = RADApplication.getInstance().currentDetailViewContext();
                if (this.genericObject !== null) {
                    const validPdoIds = this.getSubjectPdoIds(currentContext);
                    const notFound = validPdoIds.filter(x => x === this.genericObject.pdoId).length === 0;
                    if (notFound) {
                        console.log('An Error-Message for a non active DVC was repressed.');
                        return false;
                    }
                }
            }

            if (result.hasValidationMessages)
                this.showValidationMessages(result.validationMessages, false);

            if (result.isSuccessful || result.hasValidationMessages) {
                let toCommit = this.getPropertiesToCommitAfterUpdate(pendingChanges);
                this.genericObject.commit(toCommit);
                this.resetForcedUpdate();
                if (this.useShortTransactions())
                    this.internalIsModifiedForced(!result.isSuccessful || result.hasValidationMessages);

                return true;
            }

            this.resetForcedUpdate();
            return false;
        }).promise();
    }

    private getSubjectPdoIds(context: DetailViewContext): string[] {
        let result = [];

        if (!context)
            return result;

        if (context.genericObject && context.genericObject.pdoId)
            result.push(context.genericObject.pdoId);

        const nestedContexts = context._nestedDetailViewContexts();
        for (let key in nestedContexts) {
            const nestedContext = nestedContexts[key] as DetailViewContext;
            const nestedResult = this.getSubjectPdoIds(nestedContext);
            for (let nestedId of nestedResult) {
                result.push(nestedId);
            }
        }

        return result;
    }

    /**
     * Sendet ausstehende Wert-Änderungen an den Server
     * @param {[string]} propertyNames - Eine OPTIONALE Liste der Namen der zu übermittelnden Properties
     * @returns {Deferred}
     */
    private _updateAndValidateProperties(propertyNames?: Array<string>): JQueryPromise<Array<IValidationMessage>> {
        // Wenn Property-Namen übergeben werden, darf nicht der Root-Context genutzt werden!
        if (this == this.getRootDetailViewContext() || propertyNames?.length > 0) {
            return this._updateAndValidatePropertiesInternal(propertyNames);
        }

        return this.getRootDetailViewContext()._updateAndValidateProperties(propertyNames);
    }

    /**
     * Interne Methode für "_updateAndValidateProperties"
     */
    private _updateAndValidatePropertiesInternal(propertyNames?: Array<string>): JQueryPromise<Array<IValidationMessage>> {
        let pendingChanges = this.getPendingChanges();

        if (propertyNames) {
            pendingChanges = _.pick(pendingChanges, (value, key) => {
                return propertyNames.indexOf(key) > -1;
            });
        }

        let nestedContextCalls: JQueryPromise<any>[] = [];
        const nestedContexts = this.getNestedDetailViewContexts();
        for (let name in nestedContexts) {
            if (!nestedContexts.hasOwnProperty(name))
                continue;
            const nestedContextCall = nestedContexts[name]._updateAndValidatePropertiesInternal();
            nestedContextCalls.push(nestedContextCall);
        }

        return $.when.apply($, nestedContextCalls).then((...nestedResults: Array<IValidationMessage>[]) => {
            let allMessages: Array<IValidationMessage> = [].concat.apply([], nestedResults);

            return RADEnvironmentProxy.getInstance().updateAndValidateProperties({
                detailViewContextID: this.id,
                valuesToUpdate: JSON.stringify(pendingChanges),
                valuesToValidate: propertyNames || [],
                nestedContextName: this.getNestedContextPath()
            }).then((updateAndValidateDTO) => {
                const result = this.handleUpdateAndValidateDTO(updateAndValidateDTO);
                if (result.isSuccessful || result.hasValidationMessages) {
                    const toCommit = this.getPropertiesToCommitAfterUpdate(pendingChanges);
                    this.genericObject.commit(toCommit);

                    const invalidPropertyMessages = this.getInvalidPropertyMessages(result, propertyNames);
                    allMessages = allMessages.concat(invalidPropertyMessages);
                }

                if (this.useShortTransactions())
                    this.internalIsModifiedForced(!result.isSuccessful || result.hasValidationMessages);

                return allMessages;
            });
        });
    }

    private getInvalidPropertyMessages(dto: ResultDTO, propertyNames?: Array<string>): IValidationMessage[] {
        const result = dto.validationMessages ? dto.validationMessages : [];
        const invalidProperties = this.getInvalidProperties();
        let hasInvalidProperties = _.keys(invalidProperties).length > 0;

        // wenn propertyNames übergeben wurde, dann sollte ein Fehler angezeigt werden, wenn in invalides Control unter den übergebenen properties vorhanden ist
        if (propertyNames) {
            hasInvalidProperties = _.intersection(_.keys(invalidProperties), propertyNames).length > 0;
        }

        if (hasInvalidProperties) {
            _.keys(invalidProperties).forEach((key: string) => {
                result.push({
                    context: (this as any),
                    property: key,
                    message: ResourceManager.getInstance().get('PROPERTY_CONSTRAINT')
                });
            });
        }

        return result;
    }


    /**
     * Speichert den DetailViewContext
     */
    public apply(): JQueryPromise<boolean> {
        if (this !== this.getRootDetailViewContext()) {
            return this.getRootDetailViewContext().apply();
        }

        this.increaseBusyCounter();

        return RADEnvironmentProxy.getInstance().whenAllRequestsAreResponded().then(async () => {
            if (this.isDisposedOrDisposing())
                return false;

            this.resetValidationErrors();

            const allowApplyUIListener = await this.callOnBeforeApplyUIListener();
            if (allowApplyUIListener === false || this.isDisposedOrDisposing())
                return false;

            const allowApply = await this.onBeforeApply();
            if (allowApply === false || this.isDisposedOrDisposing())
                return false;

            const updateSuccess = await this.updateAndValidateProperties();

            // Nur wenn der Context valide ist (CustomValidation wurde bereits in updateAndValidateProperties
            // ausgführt) darf dieser an den Server geschickt werden
            if (updateSuccess === false || this.isDisposedOrDisposing() || !this.isValid())
                return false;

            return this.applyDetailViewContext(true);
        }).always(() => {
            this.decreaseBusyCounter();
        }).promise();
    }

    public onBeforeApply(): boolean | Promise<boolean> {
        const allowApply = typeof this.onBeforeApplyHandler === "function" ? Promise.resolve(this.onBeforeApplyHandler()) : Promise.resolve(true);
        return allowApply.then(allow => {
            if (!allow || this.isDisposedOrDisposing())
                return false;

            const interceptorPromises: Promise<OnBeforeApplyInterceptorCallbackResult>[] = [];
            for (const id in this.onBeforeApplyInterceptors) {
                const interceptor = this.onBeforeApplyInterceptors[id];
                interceptorPromises.push(interceptor.callback());

                if (!interceptor.retain)
                    this.removeOnBeforeApplyInterceptor(id);
            }
            return Promise.all(interceptorPromises).then(interceptorResults => {
                if (this.isDisposedOrDisposing())
                    return false;

                let success = true;
                const errorMessages: string[] = [];
                for (const interceptorResult of interceptorResults) {
                    if (interceptorResult.success)
                        continue;

                    success = false;
                    errorMessages.push(interceptorResult.errorMessage);
                }

                if (!success) {
                    const message = errorMessages.join('\n\n');
                    const messageBox = this.MessageBoxViewModel.createErrorMessageBoxWithoutErgnames(message);
                    RADApplication.getInstance().showMessageBox(messageBox);
                }

                return success;
            });
        });
    }

    /**
     * Determines whether an interceptor with a given identifier is already registered
     * @param id The unique identifier of the interceptor
     */
    public hasOnBeforeApplyInterceptor(id: string): boolean {
        if (this.isDisposedOrDisposing())
            return false;

        return this.onBeforeApplyInterceptors[id] != null;
    }

    /**
     * Registers an interceptor for execution before applying
     * @param id A unique identifier for the interceptor
     * @param callback The callback which should be executed before applying
     * @param retain Indicates whether the interceptor should be retained after execution
     */
    public setOnBeforeApplyInterceptor(id: string, callback: OnBeforeApplyInterceptorCallback, retain = false): void {
        if (this.isDisposedOrDisposing())
            return;

        this.onBeforeApplyInterceptors[id] = {
            retain,
            callback,
        };
    }

    /**
     * Removes an interceptor which is registered for execution before applying
     * @param id The unique identifier of the interceptor to remove
     */
    public removeOnBeforeApplyInterceptor(id: string): void {
        if (this.isDisposedOrDisposing())
            return;

        delete this.onBeforeApplyInterceptors[id];
    }

    public addOnAfterApplyInterceptor(interceptor: OnAfterApplyInterceptor): void {
        this.onAfterApplyInterceptors.push(interceptor);
    }

    /**
     * Speichert und schließt den DetailViewContext
     */
    public save(): JQueryPromise<boolean> {
        return this.saveInternal(false);
    }

    private saveInternal(showCancelButton: boolean): JQueryPromise<boolean> {
        if (this !== this.getRootDetailViewContext()) {
            return this.getRootDetailViewContext().save();
        }

        this.increaseBusyCounter();

        return RADEnvironmentProxy.getInstance().whenAllRequestsAreResponded().then(async () => {
            if (this.isDisposedOrDisposing())
                return false;

            this.resetValidationErrors();

            const allowSave = await this.callOnBeforeSaveUIListener();
            if (allowSave === false || this.isDisposedOrDisposing())
                return false;

            const allowApplyUIListener = await this.callOnBeforeApplyUIListener();
            if (allowApplyUIListener === false || this.isDisposedOrDisposing())
                return false;

            const allowApply = await this.onBeforeApply();
            if (allowApply === false || this.isDisposedOrDisposing())
                return false;

            const updateSuccess = await this.updateAndValidateProperties(null, true, showCancelButton);
            if (updateSuccess === false || this.isDisposedOrDisposing())
                return false;

            const applySuccess = await this.applyDetailViewContext(false);
            if (applySuccess === false || this.isDisposedOrDisposing())
                return false;

            this._hasObjectChanged = true;
            this._latestDetailViewAction = 'Save';

            return this.isOnStackNavigation
                ? true
                : this.closeCurrentContext();
        }).always(() => {
            this.decreaseBusyCounter();
        }).promise();
    }

    /**
     * Speichert vor einer Stack-Navigation
     */
    public saveBeforeStackNavigation(): JQueryPromise<boolean> {
        this.isOnStackNavigation = true;
        return this.save().always((success) => {
            this.isOnStackNavigation = success;
        }).promise();
    }

    /**
     * Beendet den DetailViewContext
     * @returns {JQueryPromise<boolean>}
     */
    public cancel(): JQueryPromise<boolean> {
        if (this !== this.getRootDetailViewContext()) {
            return this.getRootDetailViewContext().cancel();
        }

        if (this._isCanceling)
            return Utilities.resolveImmediately(false);

        this.increaseBusyCounter();

        return RADEnvironmentProxy.getInstance().whenAllRequestsAreResponded().then(() => {
            this._isCanceling = true;

            return $.when(this.callOnBeforeCancelUIListener()).then((allowCancel) => {
                if (allowCancel !== false) {
                    return $.when(this._revertPendingChanges()).then((revertSuccess) => {
                        if (revertSuccess !== false) {
                            this._latestDetailViewAction = 'Cancel';
                            return this.closeCurrentContext();
                        }

                        return revertSuccess;
                    });
                } else {
                    this._isCanceling = false;
                }

                return allowCancel;
            }).then((result) => {
                return result;
            }).always(() => {
                this.decreaseBusyCounter();
            });
        }).promise();
    }

    private lockExclusive(): void {
        if (this !== this.getRootDetailViewContext()) {
            this.getRootDetailViewContext().lockExclusive();
            return;
        }

        this.increaseBusyCounter();

        RADEnvironmentProxy.getInstance().whenAllRequestsAreResponded().then(() => {
            this.apply().then((success) => {
                if (!success)
                    return;

                RADEnvironmentProxy.getInstance().aquireExclusiveLock({
                    detailViewContextID: this.id,
                }).then((dto) => {
                    if (this.validateStatus(dto)) {
                        this.deactivateShortTransactions(this);

                        const links = this.createPrimaryToolBarLinks(this.detailViewContextDTO);
                        this.toolBar.primary(links);
                        this.refreshContextFromServer();
                    }
                })
            }).always(() => {
                this.decreaseBusyCounter();
            });
        });
    }

    private switchToShortTransactionUpdateMode() {
        if (!this.detailViewContextDTO.isNewObject)
            return;

        // Steuert die Modified Logik
        this.detailViewContextDTO.isNewObject = false;

        // Es sind andere Buttons sichtbar, wenn wir nicht mehr im Create Fall sind
        const links = this.createPrimaryToolBarLinks(this.detailViewContextDTO);
        this.toolBar.primary(links);
    }

    private deactivateShortTransactions(context: DetailViewContext) {
        if (!context)
            return;

        if (context.detailViewContextDTO)
            context.detailViewContextDTO.useShortTransactions = false;

        const dv = context.detailView();
        if (dv)
            dv.useShortTransactions = false;

        context.genericObject.isModified(false);
        context.useShortTransactions(false);

        const nestedContexts = context.getNestedDetailViewContexts();
        for (let name in nestedContexts) {
            this.deactivateShortTransactions(nestedContexts[name]);
        }
    }

    /**
     * Aktualisiert den Kontext
     */
    public refresh(): void {
        this._refresh(true);
    }

    public _updateGenericObjects(dvcDTO: CoreFrameDTOs.IDetailViewContextDTO): void {
        if (this !== this.getRootDetailViewContext()) {
            return this.getRootDetailViewContext()._updateGenericObjects(dvcDTO);
        }

        this._updateGenericObjectsInternal(dvcDTO);
    }

    private _updateGenericObjectsInternal(dvcDTO: CoreFrameDTOs.IDetailViewContextDTO): void {
        let subject = dvcDTO.subject;

        // merged die informationen von subject in das genericObject
        // überschreibt bestehende informationen (daten aus subject gewinnen)
        this.genericObject.refresh(subject);

        let nestedContexts = this.getNestedDetailViewContexts();
        if (nestedContexts != null) {
            for (let name in nestedContexts) {
                let nestedContext = nestedContexts[name];
                let nestedDVCDTO = dvcDTO.nestedDetailViewContexts[name];

                if (nestedContext != null && nestedDVCDTO != null) {
                    //Rekursiv!
                    nestedContext._updateGenericObjectsInternal(nestedDVCDTO);
                }
            }
        }
    }

    public refreshContextFromServer(): JQueryPromise<any> {
        if (this !== this.getRootDetailViewContext())
            return this.getRootDetailViewContext().refreshContextFromServer();

        this.increaseBusyCounter();
        return $.when(this.updateProperties()).then((updateSuccess) => {
            if (updateSuccess === false || this.isDisposed)
                return undefined;

            return RADEnvironmentProxy.getInstance().refreshDetailViewContext({
                detailViewContextID: this.id
            }).then((response) => {
                let dvcDTO = response;
                if (this.validateStatus(dvcDTO, false)) {
                    this._updateGenericObjects(dvcDTO);
                }
            });
        }).then((result) => {
            return result;
        }).always(() => {
            this.decreaseBusyCounter();
        }).promise();

    }

    /**
     * Aktualisiert die übergebenen Properties des GenericObject
     * @param propertyList
     */
    public refreshProperties(propertyList: Array<string>): JQueryPromise<any> {
        this.setFactoriesBusy(propertyList, true);

        let getPropertiesCall;

        // Unterscheidung zwischen Summary- und DetailView, da Änderungen in einer DetailView
        // in einer Transaktion offen sein können
        if (this.isSummaryViewMode()) {
            getPropertiesCall = RADEnvironmentProxy.getInstance().getProperties({
                pdoID: this.genericObject.pdoId,
                properties: propertyList
            });
        } else {
            getPropertiesCall = RADEnvironmentProxy.getInstance().getPropertiesForDetailViewContext({
                detailViewContextID: this.id,
                pdoID: this.genericObject.pdoId,
                properties: propertyList
            });
        }

        return getPropertiesCall.then((response) => {
            if (!this.validateStatus(response))
                return;
            let updatedProperties = response.returnValue;
            for (let prop in updatedProperties) {
                if (updatedProperties.hasOwnProperty(prop)) {
                    let obs = this.genericObject.getObservable(prop);
                    if (ko.isObservable(obs))
                        obs(updatedProperties[prop]);
                }
            }
        }).always(() => {
            if (this.isDisposed)
                return;

            this.setFactoriesBusy(propertyList, false);
        });
    }

    /**
     * Setzt die Factories für bestimmte Properties auf einen bestimmten Busy-State.
     * Wird für ein Property keine Factory gefunden, wird der Busy-State des gesamten Contextes angepasst.
     */
    private setFactoriesBusy(properties: string[], busy: boolean): void {
        const factoriesByTupleName: { [property: string]: ControlFactory[] } = {};
        for (const property of properties) {
            const tupleName = this.getTupleName(property);
            if (tupleName in factoriesByTupleName)
                continue;

            const factories = this.getControlFactoriesForTuple(tupleName);
            if (!factories || !factories.length) {
                // Konnte für ein Property keine Factory gefunden werden, ändern wir vorsichtshalber den Busy-State vom
                // ganzen Context. So können wir sicher gehen, dass keine Properties ungewollt vom Benutzer geändert werden
                if (busy)
                    this.increaseBusyCounter();
                else
                    this.decreaseBusyCounter();
                return;
            }

            factoriesByTupleName[tupleName] = factories;
        }

        for (const tupleName in factoriesByTupleName) {
            for (const factory of factoriesByTupleName[tupleName]) {
                factory.isBusy(busy);
            }
        }
    }

    private getControlFactoriesForTuple(tupleName: string): ControlFactory[] {
        const tupleOrTuples = this.getCreatedTuple(tupleName);
        if (!tupleOrTuples)
            return [];

        const factories: ControlFactory[] = [];
        if (_.isArray(tupleOrTuples)) {
            for (const tuple of tupleOrTuples) {
                if (tuple.factory instanceof ControlFactory) {
                    factories.push(tuple.factory);
                }
            }
        } else {
            if (tupleOrTuples.factory instanceof ControlFactory) {
                factories.push(tupleOrTuples.factory);
            }
        }
        return factories;
    }

    private getTupleName(property: string): string {
        const dotIndex = property.indexOf('.');
        if (dotIndex < 0)
            return property;

        return property.substring(0, dotIndex);
    }

    /**
     * @override
     */
    public updateProperties(): JQueryPromise<boolean> {
        if (this !== this.getRootDetailViewContext()) {
            return this.getRootDetailViewContext().updateProperties();
        }

        return this.updatePropertiesInternal();
    }

    /**
     * Interne Methode für "updateProperties"
     */
    private updatePropertiesInternal(): JQueryPromise<boolean> {
        // In einer SummaryView sollen keine Änderungen zum Server geschickt werden
        if (this.isSummaryViewMode())
            return Utilities.resolveImmediately(true);

        let args = [this._updateProperties()];
        let nestedContexts = this.getNestedDetailViewContexts();

        for (let name in nestedContexts) {
            args.push(nestedContexts[name].updatePropertiesInternal());
        }

        return $.when.apply($, args);
    }


    /**
     * Forciert ein UpdateProperties
     * @returns {Deferred}
     */
    public forceUpdateProperties(): JQueryPromise<boolean> {
        this.setForcedUpdate(true);

        return this.updateProperties();
    }

    /**
     * Resettet ein ForceUpdateProperties
     * @returns {void}
     */
    public resetForcedUpdate(): void {
        this.setForcedUpdate(false);
    }

    /**
     * Setzt rekursiv, ob es sich um ein Force-Update handelt
     * @param forcedUpdate
     */
    private setForcedUpdate(forcedUpdate: boolean) {
        if (this !== this.getRootDetailViewContext()) {
            this.getRootDetailViewContext().setForcedUpdate(forcedUpdate);
            return;
        }

        this.setForceUpdateInternal(forcedUpdate);
    }

    /**
     * Interne Methode für "setForcedUpdate"
     */
    private setForceUpdateInternal(forcedUpdate: boolean) {
        this.genericObject.isForcedToUpdate = forcedUpdate;

        let nestedContexts = this.getNestedDetailViewContexts();
        for (let name in nestedContexts) {
            if (!nestedContexts.hasOwnProperty(name))
                continue;
            nestedContexts[name].setForceUpdateInternal(forcedUpdate);
        }
    }

    /**
     * Gibt an, ob UpdateProperties forciert werden soll
     * @returns {bool}
     */
    public isForcedUpdate(): boolean {
        return this.genericObject.isForcedToUpdate && !this.isLocked();
    }

    /**
     * @override
     */
    public updateAndValidateProperties(propertyNames?: Array<string>, showMessageBox?: boolean, showValidationMessageWithCancelButton?: boolean): JQueryPromise<boolean> {
        return RADEnvironmentProxy.getInstance().whenAllRequestsAreResponded().then(() => {
            return this._updateAndValidateProperties(propertyNames).then((validationMessages) => {
                const hasValidationMessages = this.showValidationMessages(validationMessages, showMessageBox, showValidationMessageWithCancelButton);
                return !hasValidationMessages;
            });
        });
    }

    /**
     * Speichert den DetailViewContext
     * Sorgt allerdings nicht dafür, dass noch ausstehende Änderungen an den Server geschickt werden;
     * dies wird durch this.apply sichergestellt!
     * @returns {Deferred}
     */
    public applyDetailViewContext(evaluateProperties: boolean): JQueryPromise<boolean> {
        let args = [this._applyDetailViewContext(evaluateProperties)];

        /*
         * ApplyDetailViewContext nicht für NestedContexts aufrufen!
         *
         * for (let name in nestedContexts) {
         *    args.push(nestedContexts[name]._applyDetailViewContext());
         * }
         */

        return $.when.apply($, args).then((...rest) => {

            let isClosing = !evaluateProperties;
            this.notifyAfterApply(isClosing);

            let validationMessages = {};
            let status = true;

            for (let i = 0; i < rest.length; i++) {
                if (_.isObject(rest[i])) {
                    validationMessages = _.extend(validationMessages, rest[i]);
                } else if (rest[i] === false) {
                    status = false;
                }
            }

            let convertedMessages = this.convertValidationObject(validationMessages);
            let hasValidationMessages = this.showValidationMessages(convertedMessages);
            let applySuccess = !hasValidationMessages && status;

            return applySuccess;
        });
    }

    /**
     * Ruft eine Operation des DetailViewContexts auf
     * @param {string} name - Der Name der Operation
     * @param {object} parameters - An die Operation übergebene Parameter
     * @returns {Deferred}
     */
    public callInterceptableOperation(name: string, parameters: Array<any>, withValidation: boolean = true, needsLock = true): JQueryPromise<any> {
        this.increaseBusyCounter();

        return $.when(this.callOnBeforeCallOperationUIListener(name)).then((allowCallOperation) => {
            if (allowCallOperation === false)
                return undefined;

            return this.callOperation(name, parameters, withValidation, needsLock);
        }).then((result) => {
            return result;
        }).always(() => {
            this.decreaseBusyCounter();
        }).promise();
    }

    /**
     * Ruft eine Operation des DetailViewContexts auf
     * @param {string} name - Der Name der Operation
     * @param {object} parameters - An die Operation übergebene Parameter
     * @returns {Deferred}
     */
    public callOperation(name: string, parameters: Array<any>, withValidation: boolean = true, needsLock = true): JQueryPromise<any> {
        let operation = this.getOperation(name);

        this.increaseBusyCounter();

        return $.when(this.updateProperties()).then((updateSuccess) => {
            if (updateSuccess === false)
                return undefined;

            return this.callOperationUnchecked(name, parameters, withValidation, needsLock).then((operationResultDTO) => {
                let operationResultIsOk = this.handleOperationResultDTO(operation, operationResultDTO);

                this.callOnAfterCallOperationUIListener(name, operationResultDTO);

                return { operationResultIsOk, operationResultDTO };
            });
        }).then((result) => {
            if (result.operationResultDTO && this.useShortTransactions())
                this.internalIsModifiedForced(false);
            return result;
        }).then((result) => {
            let operationResultDTO = result.operationResultDTO;

            // Alle geänderten Assoziationen werden geladen, auch wenn sie Lazy sind
            AssociationHelper.activateDataLoading(this, operationResultDTO.touchedMembers);

            if (result.operationResultIsOk) {
                FrontController.evaluateDetailViewContextAfterCallOperation(this, operationResultDTO);
            }

            return result;
        }).always(() => {
            this.decreaseBusyCounter();
        }).promise();
    }

    /**
     * Ruft eine Operation des DetailViewContexts auf, ohne Verwendung von
     * - dem hinterlegten UIListener (kein onBeforeCallOperation, onAfterCallOperation)
     * - handleOperationResultDTO (keine automatische Fehler-Anzeige nach Operation)
     * - Loader
     *
     * @param {string} name - Der Name der Operation
     * @param {object} parameters - An die Operation übergebene Parameter
     * @returns {Deferred}
     */
    public callOperationUnchecked(name: string, parameters: Array<any>, withValidation: boolean = false, needsLock = true): JQueryDeferred<CoreFrameDTOs.IOperationResultDTO> {
        let operationCall: JQueryDeferred<CoreFrameDTOs.IOperationResultDTO> = this.uiListener.onCallOperation(this, name, parameters);

        if (Utilities.isNullOrUndefined(operationCall)) {
            operationCall = RADEnvironmentProxy.getInstance().callOperationFromDVContext({
                detailViewContextID: this.id,
                operationName: name,
                operationParameters: parameters,
                property_values: JSON.stringify({}),
                contextParameters: this.getContextParameters(),
                nestedContextName: this.getNestedContextPath(),
                withValidation: withValidation,
                needsLock: needsLock,
            });
        }

        return operationCall;
    }

    /**
     * Ruft den aktuellen NestedContextPath ab,
     * @returns {string} - {a}.{b}.{c}
     */
    public getNestedContextPath(): string {
        let result = '';

        if (this.__parent != null) {
            let parentNestedContextName = this.__parent.getNestedContextPath();

            if (parentNestedContextName) {
                if (result) {
                    result += '.';
                }

                result += parentNestedContextName;
            }
        }

        if (this._nestedContextName) {
            if (result) {
                result += '.';
            }

            result += this._nestedContextName;
        }

        return result;
    }

    public notifyAfterApply(isClosing: boolean): void {
        this.callOnAfterApplyUIListener(isClosing);
        for (const interceptor of this.onAfterApplyInterceptors)
            interceptor(isClosing);
    };

    public notifyRefineContextDto(): void {
        this.uiListener.refineContextDto(this);
    }

    public notifyBeforeRender(): void {
        if (this.isSummaryViewMode()) {
            this.uiListener['onBeforeRenderSummaryViewInternal'](this);
        }
        else {
            this.uiListener['onBeforeRenderInternal'](this);
        }
    }

    public notifyDetailViewReady(): void {
        this.uiListener['onDetailViewReadyInternal'](this);
    }

    public notifySummaryViewReady(): void {
        this.uiListener.onSummaryViewReady(this);
    }

    public notifyPartialRendered(): void {
        this.uiListener.onPartialRendered(this);
    }

    public notifyDetailViewEntered(hasError: boolean): void {
        Messenger.createMessageAndSend('DetailViewEnteredMessage', this.createDetailViewEnteredMessage(hasError));
    }

    /**
     * Erzeugt die DetailViewEnteredMessage
     * @param hasError
     */
    protected createDetailViewEnteredMessage(hasError: boolean): DetailViewEnteredMessage {
        return new DetailViewEnteredMessage({
            context: this,
            hasServerError: hasError || false,
            isPushed: this._contextPdoId != null, // FIXME?
            isSummaryViewMode: this._isSummaryViewMode === true
        });
    }

    public updatePageTitle(contextInfo: CoreFrameDTOs.IDetailViewContextDTO): void {
        this.pageTitle = ResourceManager.getInstance().get(contextInfo.detailView.ergName) + this.getPrincipalSuffix(contextInfo.subject);
    }

    public getPrincipalSuffix(go: CoreFrameDTOs.IGenericDTO): string {
        if (go.principalName == null || go.principalName === "") {
            return "";
        }

        if (go.isPrincipalIndependent) {
            return "";
        }

        let principal = RADApplication.getInstance().principal;
        if (principal.pdoId() == "-1" || principal.name() == go.principalName) {
            return "";
        }

        let suffix = ResourceManager.getInstance().get("DETAILVIEW_PRINCIPAL_NAME_SUFFIX");
        suffix = suffix.replace("$$principalName$$", go.principalName);

        return " " + suffix;
    }

    public raiseChangeHeader(): void {
        if (this._isSummaryViewMode == true || this.__parent != null) {
            // Anmerkung: Im SL-Client wurde für SummaryViews kein DetailViewContext angelegt; daher unterdrücken wir hier die Nachricht bei _isSummaryViewMode == true;
            // Die DetailViewEnteredMessage, die an anderer Stelle geworfen wird, ist aber durchaus nützlich.
            //
            // Ob eine ChangeHeaderTitleMessage für NestedDetailViewContext sinnvoll ist, ergibt sich aus den existierenden Anwendungsfällen noch nicht
            // ganz klar, da bisher (mir bekannt) nur folgende Situationen vorkamen:
            // - Nested-Kontexte wurden       verwendet && ChangeHeaderTitleMessage wurde nicht ausgewertet
            // - Nested-Kontexte wurden nicht verwendet && ChangeHeaderTitleMessage wurde       ausgewertet
            // Da die untergeordneten Kontexte aus meiner Sicht nicht titelgebend sein sollten (und der Titel auch von der Reihenfolge abhinge)
            // wird diese Nachricht hier unterdrückt.
            return;
        }
        let prefix = ResourceManager.getInstance().get("DETAILVIEW_PAGETITLE_PREFIX");
        let go = this.genericObject;
        if (go != null) {
            let msg = new ChangeHeaderTitleMessage({});
            if (go.isSingleton) {
                msg.title = this.pageTitle;
            }
            else {
                msg.title = this.pageTitle;
                msg.prefix = prefix;
            }
            Messenger.send(msg);
        }
    }

    /**
     * Informiert den UIListener darüber, dass die DetailView verlassen wurde
     * Informiert den Aufrufer darüber, dass die erzeugte DetailView verlassen wurde
     * @returns {Deferred}
     */
    public notifyDetailViewClosed(): void {
        if (this.__caller != null && this.__caller.notifyDetailViewClosed != null) {
            this.__caller.notifyDetailViewClosed(this.genericObject.pdTypeName, this.genericObject.pdoId);
        }

        Messenger.createMessageAndSend('DetailViewExitedMessage', this.createDetailViewExitedMessage());
        Messenger.disposeMessage('DetailViewEnteredMessage');
        Messenger.disposeMessage('DetailViewExitedMessage');

        this._hasPendingDVC = false;
        this._isContextClosed = true;

        return this.uiListener['onDetailViewClosedInternal'](this);
    }

    /**
     * Erzeugt die DetailViewExitedMessage
     */
    protected createDetailViewExitedMessage(): DetailViewExitedMessage {
        return new DetailViewExitedMessage({
            context: this,
            callId: this._callId,
            isServerError: false,
            hasObjectChanged: this._hasObjectChanged,
            leavingAction: this._latestDetailViewAction,
            hasPendingDVC: this._hasPendingDVC,
            isSummaryViewMode: this._isSummaryViewMode,
            isOnStackNavigation: this.isOnStackNavigation
        });
    }

    /**
     * @override 
     */
    public isSummaryViewMode(): boolean {
        return this._isSummaryViewMode;
    }

    public setPendingDVC(hasPendingDVC: boolean): void {
        this._hasPendingDVC = hasPendingDVC;
    }

    public getReasonForLeaving(): any {
        if (this._reasonForLeaving != null) {
            return this._reasonForLeaving;
        }
        return this._latestDetailViewAction;
    }

    public setReasonForLeaving(reason: any) {
        this._reasonForLeaving = reason;
    }

    /**
     * Schaltet alle erzeugten Factories auf ReadOnly
     */
    public reduceRights(exceptions?: Array<any>): void {
        let createdFactories = this.getCreatedControlFactories();

        let exceptionFactoryNames = _.map(_.filter(exceptions,
            (exception: any) => { return exception && exception.detailViewItem && exception.detailViewItem.name; }), // filter
            (exception: any) => { return exception.detailViewItem.name; }); // map

        for (let i = 0; i < createdFactories.length; i++) {
            let factory = createdFactories[i];

            if (factory == null || factory.isEnabled() !== true || // is not an enabled controlfactory
                exceptionFactoryNames.indexOf(factory.detailViewItem.name) > -1) { // is exception
                continue;
            }

            this._reducedRights.push(factory.detailViewItem.name);
            factory.isEnabled(false);
        }
    }

    /**
     * Schaltet alle auf ReadOnly geschalteten Factories auf Nicht-ReadOnly
     */
    public restoreRights(): void {
        let createdFactories = this.getCreatedControlFactories();

        for (let i = 0; i < createdFactories.length; i++) {
            let factory = createdFactories[i];

            if (factory != null) {
                let index = this._reducedRights.indexOf(factory.detailViewItem.name);

                if (index > -1) {
                    this._reducedRights.splice(index, 1);
                    factory.isEnabled(true);
                }
            }
        }

        this._reducedRights = [];
    }

    /**
     * Entfernt alle DefaultIteratoren
     * @returns {Deferred}
     */
    public removeDefaultIterators(): JQueryPromise<boolean[]> {
        if (_.keys(this.defaultIterators).length == 0) {
            return Utilities.resolveImmediately([]);
        }

        return RADEnvironmentProxy.getInstance().deleteIterators({
            iteratorIDs: _.values(this.defaultIterators).map((it: CoreFrameDTOs.IIteratorDTO) => parseInt(it.id))
        }).then((result: boolean[]) => {
            this.defaultIterators = {};

            return result;
        }).promise();
    }

    /***
     * Entfernt einen DefaultIterator
     * @param {string} name - Der Name des dazugehörigen Items
     * @returns {Deferred}
     */
    public removeDefaultIterator(name: string): JQueryPromise<boolean> {
        if (name in this.defaultIterators) {
            return RADEnvironmentProxy.getInstance().deleteIterator({
                iteratorID: this.defaultIterators[name].id
            }).then((result) => {
                if (this.isDisposed === false) {
                    delete (this.defaultIterators[name]);
                }

                return result;
            }).promise();
        }

        return Utilities.resolveImmediately(false);
    }

    /**
     * Erzeugt einen neuen Kontext im DetailViewContextStack des ViewModels
     * @param {DetailViewContext} caller - Der aufrufende Kontext
     * @param {ListViewDTO} listView - Ein Objekt mit Informationen über den zu erzeugenden Kontext
     * @param {string} pdoId - Die Id des zu erzeugenden Kontexts
     * @param {string} callId: CallId
     * @param {string} principalFilter - Der Filtermodus für Mandanten
     * @param {GenericObject} genericObject - Das GenericObject (Default: aus dem aktuellen Context)
     */
    public createContext(caller: any, listView: CoreFrameDTOs.IListViewDTO, pdoId?: string, callId?: string, principalFilterMode?: string, genericObject: GenericObject = null, createModal: boolean = false, reason: 'EDIT' | 'CREATE' = null): void {
        // Ein Objekt ist selektiert => Kontext für das Objekt öffnen
        if (pdoId != null) {
            if (createModal) {
                this.createContextModal(caller, listView.name, pdoId, listView.relationName, callId, principalFilterMode, genericObject, listView.filter)
            } else {
                this._createContext(caller, listView.typeName, pdoId, listView.relationName, callId, principalFilterMode, genericObject, listView.filter);
            }
            // Es gibt nur einen Typen in der ALV => Kontext für den Typen öffnen
        } else if (listView.types.length === 1) {
            if (createModal) {
                this.createContextModal(caller, listView.types[0].name, pdoId, listView.relationName, callId, principalFilterMode, genericObject, listView.filter)
            } else {
                this._createContext(caller, listView.types[0].name, pdoId, listView.relationName, callId, null, genericObject, listView.filter);
            }
            // Es gibt mehrere Typen in der ALV
        } else if (listView.types.length > 1) {
            // => Beim Erstellen Typ-Selektion anzeigen
            if (!reason || reason === 'CREATE') {
                if (this._subTypeSelectorWindowViewModel == null) {
                    this._subTypeSelectorWindowViewModel = this.TypeSelectorWindowViewModel.createSubTypeSelectorWindow(
                        listView.ergName,
                        listView.types,
                        (typeName) => {
                            if (createModal) {
                                this.createContextModal(caller, typeName, pdoId, listView.relationName, callId, principalFilterMode, genericObject, listView.filter)
                            } else {
                                this._createContext(caller, typeName, pdoId, listView.relationName, callId, null, genericObject, listView.filter);
                            }
                        },
                        this.locator,
                        () => {
                            NavigationService.getInstance().cleanRegion('Modal');
                            this._subTypeSelectorWindowViewModel = null;
                        }
                    );

                    this._subTypeSelectorWindow = new this.TypeSelectorWindow(this._subTypeSelectorWindowViewModel);
                    this._subTypeSelectorWindow.getTuple();
                }
                // => Sonst Kontext für Haupt-Typ der ALV öffnen
            } else {
                this._createContext(caller, listView.typeName, pdoId, listView.relationName, callId, principalFilterMode, genericObject, listView.filter);
            }
            // ALV Typen sind Leer (keine Create Rechte) => Kontext für Haupt-Typ der ALV öffnen, wenn kein Objekt erstellt werden soll
        } else if (reason !== 'CREATE') {
            this._createContext(caller, listView.typeName, pdoId, listView.relationName, callId, principalFilterMode, genericObject, listView.filter);
        }
    }

    /**
     * Ruft den UIListener "onBeforeCallOperation" des aktuellen DetailViewContexts auf
     * @param operation
     */
    private callOnBeforeCallOperationUIListener(operation: string): JQueryPromise<boolean> {
        const result = this.uiListener.onBeforeCallOperation(this, operation);
        if (typeof result === 'boolean')
            return Utilities.resolveImmediately(result).promise();
        else if (!Utilities.isNullOrUndefined(result) && Utilities.isFunction(result.promise))
            return result.promise();
        else
            return result as any;
    }

    /**
     * Ruft rekursiv den UIListener "onAfterCallOperation" des aktuellen DetailViewContexts auf
     * @param operation
     */
    private callOnAfterCallOperationUIListener(operation: string, result: CoreFrameDTOs.IOperationResultDTO): void {
        this.uiListener.onAfterCallOperation(this, operation, result);
    }

    /**
     * Ruft rekursiv den UIListener "onBeforeSave" des aktuellen
     * DetailViewContexts und seiner NestedDetailViewContexts auf
     * @param operation
     */
    protected callOnBeforeSaveUIListener(checkRoot: boolean = true): JQueryPromise<boolean> {
        return this.callUIListenerChain('onBeforeSave', [], this.callOnBeforeSaveUIListener, checkRoot);
    }

    /**
     * Ruft rekursiv den UIListener "onBeforeApply" des aktuellen
     * DetailViewContexts und seiner NestedDetailViewContexts auf
     * @param operation
     */
    protected callOnBeforeApplyUIListener(checkRoot: boolean = true): JQueryPromise<boolean> {
        return this.callUIListenerChain('onBeforeApplyInternal', [], this.callOnBeforeApplyUIListener, checkRoot);
    }

    /**
     * Ruft rekursiv den UIListener "onAfterApply" des aktuellen
     * DetailViewContexts und seiner NestedDetailViewContexts auf
     * @param operation
     */
    private callOnAfterApplyUIListener(isClosing: boolean, checkRoot: boolean = true): void {
        this.callUIListenerChain('onAfterApply', [isClosing], this.callOnAfterApplyUIListener, checkRoot);
    }

    /**
     * Ruft rekursiv den UIListener "onBeforeCancel" des aktuellen
     * DetailViewContexts und seiner NestedDetailViewContexts auf
     * @param operation
     */
    protected callOnBeforeCancelUIListener(checkRoot: boolean = true): JQueryPromise<boolean> {
        return this.callUIListenerChain('onBeforeCancel', [], this.callOnBeforeCancelUIListener, checkRoot);
    }

    /**
     * Hilfsmethode, die generisch die Verkettung von UI-Listenern der untergeordneten Kontexte übernimmt
     * @param methodName
     * @param parameters
     * @param nestedMethod
     * @param checkRoot Soll die Prüfung, ob die Funktion auf dem Root-Context durchgeführt wird, stattfinden?
     */
    private callUIListenerChain(methodName: string, parameters: any[], nestedMethod: Function, checkRoot: boolean): JQueryPromise<boolean> {
        if (this !== this.getRootDetailViewContext() && checkRoot) {
            return this.getRootDetailViewContext().callUIListenerChain(methodName, parameters, nestedMethod, false);
        }

        // Es werden alle Aufrufe der Nested-Contexte in einem Array gesammelt
        let nestedContextCalls: JQueryPromise<boolean>[] = [];
        const nestedContexts = this.getNestedDetailViewContexts();
        for (let name in nestedContexts) {
            if (!nestedContexts.hasOwnProperty(name))
                continue;

            // checkRoot hereinreichen, ist ab jetzt aber immer false
            checkRoot = false;
            parameters.push(checkRoot);

            const nestedContextCall = nestedMethod.apply(nestedContexts[name], parameters);
            nestedContextCalls.push(nestedContextCall);
        }

        // Wenn die Ergebnisse aller Aufrufe verfügbar sind, sind diese in "nestedResults" vorhanden
        return $.when.apply($, nestedContextCalls).then((...nestedResults: boolean[]) => {
            if (nestedResults.some(result => result === false))
                return false;
            // Der aktuelle Kontext wird immer als erster Parameter übergeben
            parameters.splice(0, 0, this);
            return this.uiListener[methodName].apply(this.uiListener, parameters);
        });
    }

    /**
     * Gibt den obersten (Root-) DetailViewContext zurück
     */
    public getRootDetailViewContext(): DetailViewContext {
        let context = this;
        while (context.getParentContext() != null) {
            context = context.getParentContext() as any;
        }

        return context;
    }

    /**
     * @override
     */
    public getLockGroupId(): number {
        return parseInt(this.detailViewContextDTO.lockGroupID);
    }

    /**
     * Versetzt die DV in den readonly Modus.
     */
    public setReadonly(): void {
        this.isLocked(true);
        this.forceReadonly(true);
    }

    /**
     * Gibt dem Server bescheid, dass die übergebenen Kontexte gelöscht werden sollen. Anschließend werden diese disposed.
     * Sollte genutzt werden, wenn während des Löschvorgangs anderweitig auf die übergebenen Kontexte zugegriffen werden
     * könnte, da die Kontexte für die Dauer des Löschvorgangs bereits als "in Löschung" markiert werden.
     * Der Status der Löschung kann über "isDeletedOrDeleting" abgefragt werden.
     */
    public static delete(contexts: DetailViewContext[]): JQueryPromise<IResultDTO> {
        if (!contexts?.length)
            return Utilities.resolveImmediately(null);

        for (const context of contexts) {
            context.isDeleting = true;
        }

        const resultHandler = (result: IResultDTO) => {
            for (const context of contexts) {
                if (context.validateStatus(result, false)) {
                    context.isDeleted = true;
                }
            }
            return result;
        };

        if (contexts.length === 1) {
            const context = contexts[0];
            return RADEnvironmentProxy.getInstance().deleteDetailViewContext({ detailViewContextID: context.id })
                .then(resultHandler)
                .promise();
        }

        const ids = contexts.map(c => c.id);
        return RADEnvironmentProxy.getInstance().deleteDetailViewContextStack({ detailViewContextIdStack: ids })
            .then(resultHandler)
            .promise();
    }

    public isDeletedOrDeleting(): boolean {
        return this.isDeleted || this.isDeleting;
    }

    protected disposeInternal(): void {
        this.removeDefaultIterators();
        if (this._subTypeSelectorWindowViewModel && !this._subTypeSelectorWindowViewModel.isDisposed) {
            this._subTypeSelectorWindowViewModel.dispose();
        }

        Messenger.disposeMessage('DetailViewEnteredMessage');

        Messenger.unregisterForMessageId('FilterChipsChanged', null, this);

        //Wir gehen hier nochmal sicher, dass der serverseitige Context abgebaut wurde.
        if (!this._isContextClosed && this.detailViewContextDTO != null) {
            let id = this.detailViewContextDTO.id;
            if (id !== "0") {
                let args = { detailViewContextID: id };
                RADEnvironmentProxy.getInstance().deleteDetailViewContext(args);
            }
            this._isContextClosed = true;
        }

        super.disposeInternal();
    }
}

ComponentManager.setViewModel('DetailViewContext', DetailViewContext, 'RADPresentation.HTMLClient.DefaultViewExtensions');